import { useState, useRef, useMemo, useEffect } from "react";
import { api } from "@/lib/api";
import {
    Package, Plus, Search, Filter, MoreHorizontal, Check, ArrowRight, ArrowLeft,
    Table2, Pencil, Trash2, Image as ImageIcon, Info, Unlink2, X, Sparkles,
    ChevronRight, FileSpreadsheet, Link2, Search as SearchIcon, Layers,
    Ruler as RulerIcon, Tags, UserRound, Shirt, AlertCircle, Loader2, ClipboardList,
    ChevronDown, GripVertical, CircleSlash,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext, useSortable, arrayMove,
    verticalListSortingStrategy, horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { useSizing, modelForProduct, findTable, countLinked, linkedProductsOf, timeAgo } from "@/lib/sizing-store";
import { useProductsQuery } from "@/lib/use-products-query";
import { getFitCopy, buildFitOptions, canonicalizeFit } from "@/lib/fit-copy";
import { PageHeader, EmptyState, PrimaryButton, SecondaryButton, DeleteConfirmDialog } from "@/components/design-system";
import ProductAddDialog from "@/components/ProductAddDialog";

// Extracted subcomponents (dialogs, filters, table primitives)
import { Stat, Th, Td, SortableTh, AssignPlaceholder } from "./products-tables/table/Primitives";
import { ProductsEmptyState } from "./products-tables/table/ProductsEmptyState";
import { AssignmentDialogShell, AssignOptionRow } from "./products-tables/dialogs/AssignmentDialogShell";
import { ProductFitDialog } from "./products-tables/dialogs/ProductFitDialog";
import { ProductModelDialog } from "./products-tables/dialogs/ProductModelDialog";
import { ProductSizingImageDialog } from "./products-tables/dialogs/ProductSizingImageDialog";
import { LinkProductsModal } from "./products-tables/dialogs/LinkProductsModal";
import { FilterChip, QuickFilter } from "./products-tables/filters/FilterPills";
import { MultiSelectFilterPopover } from "./products-tables/filters/MultiSelectFilterPopover";
import { PresenceFilterPopover } from "./products-tables/filters/PresenceFilterPopover";
import { CategoryTreeFilter } from "./products-tables/filters/CategoryTreeFilter";
import { AddTableWizard } from "./products-tables/wizard/AddTableWizard";
import { BulkActionBar } from "./products-tables/bulk/BulkActionBar";
import { BulkFitDialog } from "./products-tables/bulk/BulkFitDialog";
import { BulkModelDialog } from "./products-tables/bulk/BulkModelDialog";
import { BulkImageDialog } from "./products-tables/bulk/BulkImageDialog";
import { BulkLinkTableDialog } from "./products-tables/bulk/BulkLinkTableDialog";
import {
    gridToValueMap, valueMapToGrid, gridSmartFill, gridPaste,
    remapValuesAfterReorder, countAxisValues,
} from "./products-tables/table/grid-helpers";
import { SortableGridRow, SortableColHeader } from "./products-tables/table/SortableGridComponents";
import { ProductsPagination } from "./products-tables/table/ProductsPagination";

// ----------------- Main page -----------------
/* ============================================================================
 * KATALOG > ÜRÜNLER — Ürün Eşleştirmeleri sayfası
 * SKU → Tablo mapping yönetimi. Ekle, filtrele, toplu bağla, unknown SKU banner.
 * ============================================================================ */
export function Products() {
    const { tables, links, productsTotal, loaded } = useSizing();
    const [wizardProduct, setWizardProduct] = useState(null);
    const [editTable, setEditTable] = useState(null);
    const [editContextProduct, setEditContextProduct] = useState(null);
    const [bulkProducts, setBulkProducts] = useState(null);
    const [addProducts, setAddProducts] = useState(false);

    return (
        <TooltipProvider delayDuration={200}>
            <div className="space-y-6" data-testid="products-page">
                <PageHeader
                    icon={Package}
                    title="Ürünler"
                    description={"Widget'ların hangi SKU'larda görüneceğini yönet. SKU → Tablo eşleştirmesi burada."}
                    actions={
                        <PrimaryButton onClick={() => setAddProducts(true)} data-testid="products-add-header-btn">
                            <Plus className="h-3.5 w-3.5" /> Ürün Ekle
                        </PrimaryButton>
                    }
                />

                {!loaded ? (
                    // Skeleton — 5 shimmering rows matching real row height so
                    // the layout doesn't jump when data arrives. Prevents the
                    // "empty state flash" for tenants who actually have products.
                    <div className="rounded-2xl border border-border bg-surface overflow-hidden" data-testid="products-loading">
                        <div className="px-6 py-3.5 border-b border-border flex items-center gap-4">
                            <div className="h-3.5 w-3.5 rounded sk-shimmer" />
                            <div className="h-3 w-32 rounded sk-shimmer" />
                            <div className="h-3 w-24 rounded sk-shimmer ml-8" />
                            <div className="h-3 w-24 rounded sk-shimmer ml-6" />
                            <div className="h-3 w-24 rounded sk-shimmer ml-6" />
                            <div className="h-3 w-32 rounded sk-shimmer ml-6" />
                        </div>
                        {[0, 1, 2, 3, 4].map((i) => (
                            <div key={i} className="px-6 py-4 border-b border-border last:border-0 flex items-center gap-4" style={{ opacity: 1 - i * 0.15 }}>
                                <div className="h-3.5 w-3.5 rounded sk-shimmer" />
                                <div className="h-10 w-10 rounded-md sk-shimmer" />
                                <div className="flex-1 space-y-1.5">
                                    <div className="h-3 w-1/3 rounded sk-shimmer" />
                                    <div className="h-2.5 w-1/4 rounded sk-shimmer" />
                                </div>
                                <div className="h-6 w-20 rounded sk-shimmer" />
                                <div className="h-6 w-24 rounded sk-shimmer" />
                                <div className="h-6 w-16 rounded sk-shimmer" />
                                <div className="h-6 w-28 rounded sk-shimmer" />
                            </div>
                        ))}
                    </div>
                ) : productsTotal === 0 ? (
                    <ProductsEmptyState onAdd={() => setAddProducts(true)} />
                ) : (
                    <ProductsTable
                        productsTotal={productsTotal}
                        onAddTable={(p) => setWizardProduct(p)}
                        onOpenTable={(t, fromProduct) => { setEditTable(t); setEditContextProduct(fromProduct || null); }}
                        onBulkAction={(ids, mode) => setBulkProducts({ ids, mode })}
                    />
                )}

                <AddTableWizard
                    product={wizardProduct === "standalone" ? null : wizardProduct}
                    standalone={wizardProduct === "standalone"}
                    open={!!wizardProduct}
                    onClose={() => setWizardProduct(null)}
                />
                <EditTableDialog
                    table={editTable}
                    fromProduct={editContextProduct}
                    open={!!editTable}
                    onClose={() => { setEditTable(null); setEditContextProduct(null); }}
                />
                <BulkLinkDialog
                    payload={bulkProducts}
                    onClose={() => setBulkProducts(null)}
                />
                <Dialog open={addProducts} onOpenChange={(v) => !v && setAddProducts(false)}>
                    <DialogContent className="max-w-xl" data-testid="add-products-dialog">
                        <DialogTitle className="text-lg font-display font-semibold">Ürün Ekle</DialogTitle>
                        <DialogDescription className="text-xs text-muted-foreground">
                            SKU'ları yaz veya ürün URL'i yapıştır. Aynı SKU otomatik tekilleştirilir.
                        </DialogDescription>
                        <ProductAddDialog
                            onAdd={() => setAddProducts(false)}
                            onCancel={() => setAddProducts(false)}
                        />
                    </DialogContent>
                </Dialog>
            </div>
        </TooltipProvider>
    );
}

/* ============================================================================
 * KATALOG > TABLOLAR — Beden tablosu yönetimi sayfası
 * Bağımsız oluştur, düzenle, taslak/yayınla, bağlı ürünleri yönet.
 * ============================================================================ */
export function Tables() {
    const [wizardProduct, setWizardProduct] = useState(null);
    const [editTable, setEditTable] = useState(null);

    return (
        <TooltipProvider delayDuration={200}>
            <div className="space-y-6" data-testid="tables-page">
                <PageHeader
                    icon={Table2}
                    title="Tablolar"
                    description="Beden ölçü tablolarını oluştur, düzenle ve yönet. Bir tablo birden çok ürüne bağlanabilir."
                    actions={
                        <PrimaryButton onClick={() => setWizardProduct("standalone")} data-testid="tables-add-header-btn">
                            <Plus className="h-3.5 w-3.5" /> Yeni Beden Tablosu
                        </PrimaryButton>
                    }
                />

                <TablesView
                    onOpen={(t) => setEditTable(t)}
                    onCreate={() => setWizardProduct("standalone")}
                />

                <AddTableWizard
                    product={wizardProduct === "standalone" ? null : wizardProduct}
                    standalone={wizardProduct === "standalone"}
                    open={!!wizardProduct}
                    onClose={() => setWizardProduct(null)}
                />
                <EditTableDialog
                    table={editTable}
                    fromProduct={null}
                    open={!!editTable}
                    onClose={() => setEditTable(null)}
                />
            </div>
        </TooltipProvider>
    );
}

/* ============================================================================
 * Legacy default export — retained for backward compatibility.
 * `/app/sizing/products` route redirects to `/app/catalog/products` in App.js.
 * ============================================================================ */
export default function ProductsTables() {
    const { tables, productsTotal } = useSizing();
    const [wizardProduct, setWizardProduct] = useState(null);
    const [editTable, setEditTable] = useState(null);
    const [editContextProduct, setEditContextProduct] = useState(null);
    const [tab, setTab] = useState("products");
    const [bulkProducts, setBulkProducts] = useState(null);
    const [addProducts, setAddProducts] = useState(false);

    return (
        <TooltipProvider delayDuration={200}>
            <div className="space-y-6" data-testid="products-tables-page">
                <div>
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-lg border border-border bg-muted/40 grid place-items-center">
                            <Package className="h-4 w-4" strokeWidth={1.75} />
                        </div>
                        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Ürünler & Tablolar</h1>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                        Widget'ların hangi SKU'larda görüneceğini yönet, tablolarını tek yerden düzenle.
                    </p>
                </div>

                <Tabs value={tab} onValueChange={setTab} data-testid="products-tables-tabs">
                    <TabsList className="bg-transparent p-0 h-auto gap-1 border-b border-border w-full justify-start rounded-none">
                        <TabsTrigger value="products" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground text-muted-foreground rounded-none border-b-2 border-transparent data-[state=active]:border-foreground px-4 pb-2.5 pt-2 text-sm font-medium" data-testid="tab-products">
                            Ürün Eşleştirmeleri <span className="ml-1.5 text-[10px] font-mono num">{productsTotal}</span>
                        </TabsTrigger>
                        <TabsTrigger value="tables" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground text-muted-foreground rounded-none border-b-2 border-transparent data-[state=active]:border-foreground px-4 pb-2.5 pt-2 text-sm font-medium" data-testid="tab-tables">
                            Tablolar <span className="ml-1.5 text-[10px] font-mono num">{tables.length}</span>
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="products" className="mt-5 space-y-4">
                        {productsTotal === 0 ? (
                            <ProductsEmptyState onAdd={() => setAddProducts(true)} />
                        ) : (
                            <ProductsTable
                                productsTotal={productsTotal}
                                onAddTable={(p) => setWizardProduct(p)}
                                onOpenTable={(t, fromProduct) => { setEditTable(t); setEditContextProduct(fromProduct || null); }}
                                onBulkAction={(ids, mode) => setBulkProducts({ ids, mode })}
                            />
                        )}
                    </TabsContent>

                    <TabsContent value="tables" className="mt-5">
                        <TablesView onOpen={(t) => { setEditTable(t); setEditContextProduct(null); }} onCreate={() => setWizardProduct("standalone")} />
                    </TabsContent>
                </Tabs>

                <AddTableWizard
                    product={wizardProduct === "standalone" ? null : wizardProduct}
                    standalone={wizardProduct === "standalone"}
                    open={!!wizardProduct}
                    onClose={() => setWizardProduct(null)}
                />
                <EditTableDialog
                    table={editTable}
                    fromProduct={editContextProduct}
                    open={!!editTable}
                    onClose={() => { setEditTable(null); setEditContextProduct(null); }}
                />
                <BulkLinkDialog
                    payload={bulkProducts}
                    onClose={() => setBulkProducts(null)}
                />
                <Dialog open={addProducts} onOpenChange={(v) => !v && setAddProducts(false)}>
                    <DialogContent className="max-w-xl" data-testid="add-products-dialog">
                        <DialogTitle className="text-lg font-display font-semibold">Ürün Ekle</DialogTitle>
                        <DialogDescription className="text-xs text-muted-foreground">
                            SKU'ları yaz veya ürün URL'i yapıştır. Aynı SKU otomatik tekilleştirilir.
                        </DialogDescription>
                        <ProductAddDialog
                            onAdd={() => setAddProducts(false)}
                            onCancel={() => setAddProducts(false)}
                        />
                    </DialogContent>
                </Dialog>
            </div>
        </TooltipProvider>
    );
}

// ----------------- Unknown SKU banner (discovery from widget_query_log) -----------------
const UnknownSkuBanner = () => {
    const { fetchUnknownSkus, dismissUnknownSkus, addProductsBulk } = useSizing();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [dismissing, setDismissing] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const data = await fetchUnknownSkus();
                if (alive) setItems(data?.items || []);
            } catch (e) {
                console.error("[unknown-sku] load failed:", e?.response?.status, e?.message);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [fetchUnknownSkus]);

    if (loading || items.length === 0) return null;

    const handleAddAll = async () => {
        try {
            await addProductsBulk({ items: items.map((r) => ({ sku: r.sku })), tableId: null });
            toast.success(`${items.length} SKU panele eklendi`);
            setItems([]);
        } catch (e) {
            toast.error("Eklenemedi");
        }
    };
    const handleDismissAll = async () => {
        setDismissing(true);
        try {
            await dismissUnknownSkus(items.map((r) => r.sku));
            setItems([]);
        } finally { setDismissing(false); }
    };

    const shown = expanded ? items : items.slice(0, 3);

    return (
        <div className="rounded-xl border border-info/30 bg-info-soft/40 p-4" data-testid="unknown-sku-banner">
            <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-info-soft grid place-items-center shrink-0">
                    <AlertCircle className="h-4 w-4 text-info" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-foreground">
                            {items.length} bilinmeyen SKU keşfedildi
                        </div>
                        <Badge variant="outline" className="text-[10px] border-info/40 text-info bg-info-soft">son 30 gün</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                        Widget bu SKU'ları ping'ledi ama panelde eşleşmeleri yok. Ekleyerek beden tablosunu göster.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {shown.map((r) => (
                            <span
                                key={r.sku}
                                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 text-[11px] font-mono"
                                data-testid={`unknown-sku-${r.sku}`}
                            >
                                {r.sku}
                                <span className="text-[9px] text-muted-foreground">×{r.hits}</span>
                            </span>
                        ))}
                        {items.length > 3 && (
                            <button
                                onClick={() => setExpanded((v) => !v)}
                                className="text-[11px] text-info hover:underline"
                                data-testid="unknown-sku-toggle"
                            >
                                {expanded ? "daha az" : `+${items.length - 3} tane daha`}
                            </button>
                        )}
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                        <Button size="sm" className="h-7 text-[11px] gap-1.5 bg-info hover:bg-info/90 text-white" onClick={handleAddAll} data-testid="unknown-add-all">
                            <Plus className="h-3 w-3" /> Tümünü Panele Ekle
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-muted-foreground" onClick={handleDismissAll} disabled={dismissing} data-testid="unknown-dismiss-all">
                            Yoksay
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ----------------- Products tab -----------------

/**
 * ProductStatusFilter — small segmented control (Tümü / Aktif / Pasif).
 * Kept in this file (Products.jsx) since it's a one-shot UI matching the
 * Beden Tabloları page's status filter, not something worth extracting.
 */
const ProductStatusFilter = ({ value, onChange }) => (
    <div className="inline-flex rounded-md border border-border p-0.5 bg-surface" data-testid="product-status-filter">
        {[
            { key: "all",     label: "Tümü" },
            { key: "active",  label: "Aktif" },
            { key: "passive", label: "Pasif" },
        ].map((opt) => (
            <button
                key={opt.key}
                onClick={() => onChange(opt.key)}
                className={`h-8 px-3 text-[11px] font-medium rounded-sm transition-colors ${value === opt.key ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`product-status-filter-${opt.key}`}
                aria-pressed={value === opt.key}
            >
                {opt.label}
            </button>
        ))}
    </div>
);

/**
 * ProductStatusToggle — per-row Aktif/Pasif switch. Görsel olarak Beden
 * Tabloları sayfasındaki row toggle ile birebir aynı: label solda, switch
 * sağda; renkler `text-foreground` (aktif) / `text-muted-foreground` (pasif).
 * XML-kaynaklı pasiflikler tooltip ile ayrışır, renk aynı kalır.
 */
const ProductStatusToggle = ({ product, onChange }) => {
    const isActive = !product.archived;
    const isManual = product.archived_reason === "manual";
    const title = isActive
        ? "Ürün widget'ta gösteriliyor"
        : isManual
            ? "Manuel pasife alındı"
            : "XML akışında bulunamadı — otomatik pasif";
    return (
        <div className="inline-flex items-center gap-2" data-testid={`product-status-toggle-${product.id}`} title={title}>
            <span
                className={`text-[10.5px] font-medium ${isActive ? "text-foreground" : "text-muted-foreground"}`}
                data-testid={`product-status-label-${product.id}`}
            >
                {isActive ? "Aktif" : "Pasif"}
            </span>
            <Switch
                checked={isActive}
                onCheckedChange={onChange}
                aria-label={isActive ? "Ürünü pasife al" : "Ürünü aktife al"}
                data-testid={`product-status-switch-${product.id}`}
            />
        </div>
    );
};

const ProductsTable = ({ onAddTable, onOpenTable, onBulkAction, productsTotal }) => {
    const { models, products, tables, links, fitLabels, images, setProductSizingExempt, linkProduct, productsVersion, bumpProductsVersion } = useSizing();
    const [searchParams, setSearchParams] = useSearchParams();

    // --- URL → initial state ------------------------------------------------
    // Bir kez okur, state'leri hydrate eder. Sonradan state → URL yazarken bu
    // değerler değişmez (initializer sadece ilk render'da çalışır).
    const presenceFromUrl = (key) => {
        const v = (searchParams.get(key) || "all").toLowerCase();
        return ["all", "has", "none", "exempt"].includes(v) ? v : "all";
    };
    const statusFromUrl = () => {
        const v = (searchParams.get("statusFilter") || "all").toLowerCase();
        return ["all", "active", "passive"].includes(v) ? v : "all";
    };
    const [selected, setSelected] = useState(new Set());
    // Bulk-action dialog state — one per axis. `bulkCreateOpen` reuses the
    // AddTableWizard in standalone mode with the current selection as
    // `initialPickedIds`.
    const [bulkFitOpen, setBulkFitOpen] = useState(false);
    const [bulkModelOpen, setBulkModelOpen] = useState(false);
    const [bulkImageOpen, setBulkImageOpen] = useState(false);
    const [bulkLinkTableOpen, setBulkLinkTableOpen] = useState(false);
    const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
    // { targets: Product[], withTable: Product[] } while a Muaf-mark
    // confirmation is on screen (only used when tabloya bağlı ürünler var).
    const [muafConfirm, setMuafConfirm] = useState(null);
    // Search input — user-typed. Debounced into `queryDebounced` before
    // hitting the server so we don't fire a request per keystroke.
    const [queryInput, setQueryInput] = useState("");
    const [queryDebounced, setQueryDebounced] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setQueryDebounced(queryInput.trim()), 350);
        return () => clearTimeout(t);
    }, [queryInput]);
    // Six independent filters — combined with AND semantics. Table / Model /
    // Fit each support a presence tri-state (all/has/none) + optional specific
    // multi-select. Group / Series / Category are plain multi-selects.
    const [tableFilter, setTableFilter] = useState(() => ({ presence: presenceFromUrl("tablePresence"), ids: [] }));
    const [groupFilter, setGroupFilter] = useState([]);   // string[] of group labels
    const [seriesFilter, setSeriesFilter] = useState([]); // string[] of series labels
    const [modelFilter, setModelFilter] = useState(() => ({ presence: presenceFromUrl("modelPresence"), ids: [] }));
    const [fitFilter, setFitFilter] = useState(() => ({ presence: presenceFromUrl("fitPresence"), ids: [] }));
    const [imageFilter, setImageFilter] = useState(() => ({ presence: presenceFromUrl("imagePresence") }));
    const [categoryFilter, setCategoryFilter] = useState([]); // string[] of segment tokens
    // Aktif / Pasif filtresi — default "all" (Beden Tabloları'ndaki UX ile
    // aynı). Chip yalnızca kullanıcı Aktif/Pasif'e tıkladığında görünür.
    const [productStatusFilter, setProductStatusFilter] = useState(statusFromUrl); // all | active | passive
    const [modelTarget, setModelTarget] = useState(null);
    const [fitTarget, setFitTarget] = useState(null);
    const [imageTarget, setImageTarget] = useState(null);
    // Sort state — server-side. Keys map 1:1 to the paginated /products
    // endpoint. Third click on a column resets sort to natural (name asc).
    const [sortState, setSortState] = useState({ key: null, dir: "asc" });
    // Pagination — server-side, 1-indexed for UX.
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);

    // Kick page back to 1 whenever a filter/search/sort changes so the user
    // isn't stranded on page 12 of a suddenly-smaller result set.
    useEffect(() => {
        setPage(1);
        setSelected(new Set());
    }, [
        queryDebounced,
        tableFilter.presence, tableFilter.ids.join(","),
        modelFilter.presence, modelFilter.ids.join(","),
        fitFilter.presence, fitFilter.ids.join(","),
        imageFilter.presence,
        groupFilter.join(","), seriesFilter.join(","),
        categoryFilter.join(","),
        productStatusFilter,
        sortState.key, sortState.dir,
        limit,
    ]);

    // ─── State → URL sync (replace mode, no history spam) ─────────────────
    // Yalnızca filtre eksenleri URL'ye yansır: statusFilter, tablePresence,
    // modelPresence, fitPresence, imagePresence. Multi-select ID'ler ve metin
    // araması URL'ye yazılmaz (kirlilik yapmasın; chip'ler zaten UI'da).
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        const setOrDel = (key, value, defaultVal) => {
            if (!value || value === defaultVal) next.delete(key);
            else next.set(key, value);
        };
        setOrDel("statusFilter", productStatusFilter, "all");
        setOrDel("tablePresence", tableFilter.presence, "all");
        setOrDel("modelPresence", modelFilter.presence, "all");
        setOrDel("fitPresence", fitFilter.presence, "all");
        setOrDel("imagePresence", imageFilter.presence, "all");
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        productStatusFilter,
        tableFilter.presence,
        modelFilter.presence,
        fitFilter.presence,
        imageFilter.presence,
    ]);

    // Server-side query. `items` on this component now refers to the CURRENT
    // PAGE only — do NOT mistake for full catalog.
    const {
        items,
        total,
        loading,
        refetch,
        patchLocalItem,
        removeLocalItems,
    } = useProductsQuery({
        page,
        limit,
        q: queryDebounced,
        sort: sortState.key || "name",
        dir: sortState.dir === "down" ? "desc" : "asc",
        tablePresence: tableFilter.presence,
        modelPresence: modelFilter.presence,
        fitPresence: fitFilter.presence,
        imagePresence: imageFilter.presence,
        tableIds: tableFilter.ids,
        modelIds: modelFilter.ids,
        fitIds: fitFilter.ids,
        groupLabels: groupFilter,
        seriesLabels: seriesFilter,
        categoryPaths: categoryFilter,
        statusFilter: productStatusFilter,
        version: productsVersion,
    });

    // Filter option pools — derived from live data so the popovers always
    // reflect what actually exists in the catalog.
    const tableOptions = useMemo(
        () => tables.map((t) => ({ id: t.id, name: t.name })),
        [tables]
    );
    const groupOptions = useMemo(() => {
        const seen = new Set();
        return tables
            .map((t) => t.groupLabel)
            .filter((lbl) => { if (!lbl || seen.has(lbl)) return false; seen.add(lbl); return true; })
            .map((lbl) => ({ id: lbl, name: lbl }));
    }, [tables]);
    const seriesOptions = useMemo(() => {
        const seen = new Set();
        return tables
            .map((t) => t.seriesLabel)
            .filter((lbl) => { if (!lbl || seen.has(lbl)) return false; seen.add(lbl); return true; })
            .map((lbl) => ({ id: lbl, name: lbl }));
    }, [tables]);
    const modelOptions = useMemo(
        () => models.map((m) => ({ id: m.id, name: m.name })),
        [models]
    );
    // Fit options are dynamic (merchant defines them under Kalıp Bilgileri).
    // buildFitOptions returns [{ storedAs, label, advice, canon }] — we use the
    // canonical key (slim/normal/relaxed) as the filter id since product.fit
    // may hold either the alias (dar/bol) OR the canonical key.
    // canonicalizeFit() collapses aliases so filtering stays deterministic.
    const fitOptions = useMemo(() => {
        const opts = buildFitOptions(fitLabels) || [];
        const seen = new Set();
        const out = [];
        opts.forEach((o) => {
            if (!o?.canon || seen.has(o.canon)) return;
            seen.add(o.canon);
            out.push({ id: o.canon, name: o.label });
        });
        return out;
    }, [fitLabels]);
    // Server does all filtering/sorting/searching — the `items` returned by
    // useProductsQuery IS the visible list for the current page.
    const visible = items;
    // Compat shim for the search box (existing UI reads `query` for chips).
    const query = queryInput;
    const setQuery = setQueryInput;
    const hasAnyFilter =
        !!query ||
        tableFilter.presence !== "all" || tableFilter.ids.length > 0 ||
        groupFilter.length > 0 || seriesFilter.length > 0 ||
        modelFilter.presence !== "all" || modelFilter.ids.length > 0 ||
        fitFilter.presence !== "all" || fitFilter.ids.length > 0 ||
        imageFilter.presence !== "all" ||
        categoryFilter.length > 0 ||
        productStatusFilter !== "all";

    const clearAll = () => {
        setQuery("");
        setTableFilter({ presence: "all", ids: [] });
        setGroupFilter([]);
        setSeriesFilter([]);
        setModelFilter({ presence: "all", ids: [] });
        setFitFilter({ presence: "all", ids: [] });
        setImageFilter({ presence: "all" });
        setCategoryFilter([]);
        setProductStatusFilter("all");
    };

    // Aktif ↔ Pasif toggle for a single product. Optimistic UI: patch the row
    // in place first, then POST. On failure roll back + toast.
    // Passive-product widgets disappear from the merchant site; passive
    // products also stay passive across XML syncs (archived_reason: "manual").
    const [statusConfirm, setStatusConfirm] = useState(null); // {product}
    const commitStatusToggle = async (product, nextActive) => {
        const previousArchived = !!product.archived;
        // Optimistic patch
        patchLocalItem(product.id, {
            archived: !nextActive,
            archived_reason: nextActive ? null : "manual",
        });
        try {
            await api.patch(`/products/${product.id}/status`, { active: nextActive });
            toast.success(nextActive ? "Ürün aktife alındı." : "Ürün pasife alındı.");
            bumpProductsVersion?.();
        } catch (e) {
            patchLocalItem(product.id, {
                archived: previousArchived,
                archived_reason: product.archived_reason,
            });
            toast.error(e?.response?.data?.detail || "Durum güncellenemedi.");
        }
    };
    const askStatusToggle = (product, nextActive) => {
        // Pasife alma → confirmation (tabloya bağlı ürünler için widget kesilir).
        // Aktife alma → doğrudan uygula (geri döndürülebilir işlem).
        if (!nextActive) setStatusConfirm({ product });
        else commitStatusToggle(product, true);
    };

    const toggle = (id) => {
        setSelected((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const toggleAll = () => {
        setSelected((s) => s.size === visible.length ? new Set() : new Set(visible.map((p) => p.id)));
    };
    const clearSelection = () => setSelected(new Set());

    // Materialised selection. Kept as an array (not Set) for downstream
    // consumers (bulk dialogs iterate over full product objects).
    const selectedProducts = useMemo(
        () => Array.from(selected).map((id) => products.find((p) => p.id === id)).filter(Boolean),
        [selected, products]
    );
    const anyExempt = selectedProducts.some((p) => p.sizingTableExempt);
    const allExempt = selectedProducts.length > 0 && selectedProducts.every((p) => p.sizingTableExempt);

    // Muaf bulk mark — SAFETY CRITICAL. Exempt=true silently clears
    // tableId server-side, so if any target already has a table link we
    // pause and require explicit merchant confirmation showing exactly
    // which links would be lost. Safe path (no links to lose) applies
    // immediately.
    //
    // Both paths end up calling `applyBulkMuafMark`, which snapshots the
    // BEFORE state (exempt flag + previous tableId) and fires an undo toast.
    const applyBulkMuafMark = (targets) => {
        // Snapshot both axes we're about to mutate — exempt flips true, and
        // any linked tableId gets cleared.  The undo restores both.
        const snapshot = targets.map((p) => ({
            id: p.id,
            prevExempt: !!p.sizingTableExempt,
            prevTableId: links[p.id] || null,
        }));
        targets.forEach((p) => setProductSizingExempt(p.id, true));
        toast.success(`${targets.length} ürün muaf olarak işaretlendi`, {
            duration: 5000,
            action: {
                label: "Geri Al",
                onClick: () => {
                    // Order matters: first restore exempt=false (backend
                    // no-op if we then also restore the link right after),
                    // then re-link products that had a table before.
                    snapshot.forEach((s) => {
                        setProductSizingExempt(s.id, s.prevExempt);
                        if (s.prevTableId) linkProduct(s.id, s.prevTableId);
                    });
                    toast.success(`${snapshot.length} ürün eski haline döndü`);
                },
            },
        });
    };

    const bulkMarkExempt = () => {
        const targets = selectedProducts.filter((p) => !p.sizingTableExempt);
        if (targets.length === 0) { toast.info("Zaten hepsi muaf"); return; }
        // Guard: any target currently linked to a table? Confirm first.
        const withTable = targets.filter((p) => links[p.id]);
        if (withTable.length > 0) {
            setMuafConfirm({ targets, withTable });
            return;
        }
        applyBulkMuafMark(targets);
    };
    const bulkUnmarkExempt = () => {
        const targets = selectedProducts.filter((p) => p.sizingTableExempt);
        if (targets.length === 0) { toast.info("Muaf ürün yok"); return; }
        const snapshot = targets.map((p) => ({ id: p.id, prevExempt: true }));
        targets.forEach((p) => setProductSizingExempt(p.id, false));
        toast.success(`${targets.length} üründen muaf işareti kaldırıldı`, {
            duration: 5000,
            action: {
                label: "Geri Al",
                onClick: () => {
                    snapshot.forEach((s) => setProductSizingExempt(s.id, s.prevExempt));
                    toast.success(`${snapshot.length} ürün tekrar muaf`);
                },
            },
        });
    };

    return (
        <>
            <div className="rounded-xl border border-border bg-surface overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[280px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ürün adı veya SKU ile ara…" className="h-9 pl-9 text-xs bg-background w-full" data-testid="products-search" />
                    </div>
                    <PresenceFilterPopover
                        label="Tablo"
                        presenceLabels={{ has: "Var", none: "Yok" }}
                        extraStates={[{ key: "exempt", label: "Muaf", disablesList: true }]}
                        options={tableOptions}
                        value={tableFilter}
                        onChange={setTableFilter}
                        keyOf={(o) => o.id}
                        labelOf={(o) => o.name}
                        emptyLabel="Kayıtlı tablo yok"
                        testId="pfilter-table"
                    />
                    <MultiSelectFilterPopover
                        label="Ölçü Grubu"
                        options={groupOptions}
                        selected={groupFilter}
                        onChange={setGroupFilter}
                        keyOf={(o) => o.id}
                        labelOf={(o) => o.name}
                        emptyLabel="Kayıtlı grup yok"
                        testId="pfilter-group"
                    />
                    <MultiSelectFilterPopover
                        label="Beden Serisi"
                        options={seriesOptions}
                        selected={seriesFilter}
                        onChange={setSeriesFilter}
                        keyOf={(o) => o.id}
                        labelOf={(o) => o.name}
                        emptyLabel="Kayıtlı seri yok"
                        testId="pfilter-series"
                    />
                    <PresenceFilterPopover
                        label="Model"
                        presenceLabels={{ has: "Var", none: "Yok" }}
                        options={modelOptions}
                        value={modelFilter}
                        onChange={setModelFilter}
                        keyOf={(o) => o.id}
                        labelOf={(o) => o.name}
                        emptyLabel="Kayıtlı model yok"
                        testId="pfilter-model"
                    />
                    <PresenceFilterPopover
                        label="Kalıp"
                        presenceLabels={{ has: "Var", none: "Yok" }}
                        options={fitOptions}
                        value={fitFilter}
                        onChange={setFitFilter}
                        keyOf={(o) => o.id}
                        labelOf={(o) => o.name}
                        emptyLabel="Tanımlı kalıp yok"
                        testId="pfilter-fit"
                    />
                    <CategoryTreeFilter
                        value={categoryFilter}
                        onChange={setCategoryFilter}
                        testId="pfilter-category"
                    />
                    {/* Aktif / Pasif filtresi — Beden Tabloları'ndakiyle aynı UX */}
                    <ProductStatusFilter value={productStatusFilter} onChange={setProductStatusFilter} />
                </div>

                {/* Active filter chips + clear-all. Only rendered while at
                    least one filter is engaged (product count moved below the
                    table so pagination can dock there). */}
                {hasAnyFilter && (
                <div className="px-4 py-2.5 border-b border-border bg-muted/20 flex items-center gap-1.5 flex-wrap" data-testid="active-filter-chips">
                    {query && (
                        <FilterChip label={`Ara: "${query}"`} onClear={() => setQuery("")} testId="chip-query" />
                    )}
                    {tableFilter.presence === "has" && tableFilter.ids.length === 0 && (
                        <FilterChip label="Tablo: Var" onClear={() => setTableFilter({ ...tableFilter, presence: "all" })} testId="chip-table-presence" />
                    )}
                    {tableFilter.presence === "none" && (
                        <FilterChip label="Tablo: Yok" onClear={() => setTableFilter({ presence: "all", ids: [] })} testId="chip-table-presence" />
                    )}
                    {tableFilter.presence === "exempt" && (
                        <FilterChip label="Tablo: Muaf" onClear={() => setTableFilter({ presence: "all", ids: [] })} testId="chip-table-presence" />
                    )}
                    {tableFilter.ids.map((tid) => {
                        const t = tables.find((x) => x.id === tid);
                        return <FilterChip key={tid} label={`Tablo: ${t?.name || tid}`} onClear={() => setTableFilter({ ...tableFilter, ids: tableFilter.ids.filter((x) => x !== tid) })} testId={`chip-table-${tid}`} />;
                    })}
                    {groupFilter.map((g) => (
                        <FilterChip key={g} label={`Grup: ${g}`} onClear={() => setGroupFilter(groupFilter.filter((x) => x !== g))} testId={`chip-group-${g}`} />
                    ))}
                    {seriesFilter.map((s) => (
                        <FilterChip key={s} label={`Seri: ${s}`} onClear={() => setSeriesFilter(seriesFilter.filter((x) => x !== s))} testId={`chip-series-${s}`} />
                    ))}
                    {modelFilter.presence === "has" && modelFilter.ids.length === 0 && (
                        <FilterChip label="Model: Var" onClear={() => setModelFilter({ ...modelFilter, presence: "all" })} testId="chip-model-presence" />
                    )}
                    {modelFilter.presence === "none" && (
                        <FilterChip label="Model: Yok" onClear={() => setModelFilter({ presence: "all", ids: [] })} testId="chip-model-presence" />
                    )}
                    {modelFilter.ids.map((mid) => {
                        const m = models.find((x) => x.id === mid);
                        return <FilterChip key={mid} label={`Model: ${m?.name || mid}`} onClear={() => setModelFilter({ ...modelFilter, ids: modelFilter.ids.filter((x) => x !== mid) })} testId={`chip-model-${mid}`} />;
                    })}
                    {fitFilter.presence === "has" && fitFilter.ids.length === 0 && (
                        <FilterChip label="Kalıp: Var" onClear={() => setFitFilter({ ...fitFilter, presence: "all" })} testId="chip-fit-presence" />
                    )}
                    {fitFilter.presence === "none" && (
                        <FilterChip label="Kalıp: Yok" onClear={() => setFitFilter({ presence: "all", ids: [] })} testId="chip-fit-presence" />
                    )}
                    {fitFilter.ids.map((k) => {
                        const f = fitOptions.find((x) => x.id === k);
                        return <FilterChip key={k} label={`Kalıp: ${f?.name || k}`} onClear={() => setFitFilter({ ...fitFilter, ids: fitFilter.ids.filter((x) => x !== k) })} testId={`chip-fit-${k}`} />;
                    })}
                    {imageFilter.presence === "has" && (
                        <FilterChip label="Görsel: Var" onClear={() => setImageFilter({ presence: "all" })} testId="chip-image-presence" />
                    )}
                    {imageFilter.presence === "none" && (
                        <FilterChip label="Görsel: Yok" onClear={() => setImageFilter({ presence: "all" })} testId="chip-image-presence" />
                    )}
                    {categoryFilter.map((path) => {
                        const isUncategorized = path === "__uncategorized__";
                        const parts = path.split(" > ");
                        const leaf = parts[parts.length - 1];
                        // If a parent path is also selected the leaf alone
                        // could be ambiguous — but the merchant already sees
                        // the full stack in the popover, so a leaf chip stays
                        // readable while `title` exposes the full path.
                        return (
                            <FilterChip
                                key={path}
                                label={isUncategorized ? "Kategori: Yok" : `Kategori: ${leaf}`}
                                title={isUncategorized ? "Kategorisi olmayan ürünler" : path}
                                onClear={() => setCategoryFilter(categoryFilter.filter((x) => x !== path))}
                                testId={`chip-category-${path}`}
                            />
                        );
                    })}
                    {productStatusFilter !== "all" && (
                        <FilterChip
                            label={`Durum: ${productStatusFilter === "active" ? "Aktif" : "Pasif"}`}
                            title="Aktif/Pasif filtresi"
                            onClear={() => setProductStatusFilter("all")}
                            testId="chip-product-status"
                        />
                    )}
                    {hasAnyFilter && (
                        <button
                            onClick={clearAll}
                            className="ml-auto text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                            data-testid="clear-all-filters"
                        >
                            Filtreleri temizle
                        </button>
                    )}
                </div>
                )}
                <div className={`overflow-x-auto relative transition-opacity ${loading ? "opacity-70" : "opacity-100"}`}>
                    {loading && (
                        <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden pointer-events-none z-10" data-testid="products-loading-bar">
                            <div className="absolute inset-y-0 w-2/5 bg-gradient-to-r from-transparent via-foreground/75 to-transparent animate-indeterminate" />
                        </div>
                    )}
                    {visible.length === 0 ? (
                        <div className="px-6 py-16 text-center" data-testid="products-empty-filtered">
                            <div className="mx-auto h-12 w-12 rounded-xl border border-border bg-muted/40 grid place-items-center mb-4">
                                <Package className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {loading ? "Yükleniyor…" : hasAnyFilter ? "Filtrelere uyan ürün yok." : "Henüz ürün yok."}
                            </p>
                            {hasAnyFilter && !loading && (
                                <button
                                    onClick={clearAll}
                                    className="mt-3 text-xs text-brand hover:underline"
                                    data-testid="empty-clear-filters"
                                >
                                    Filtreleri temizle
                                </button>
                            )}
                        </div>
                    ) : (
                    <table className="w-full text-sm" data-testid="products-table">
                        <thead>
                            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                <th className="px-4 py-3 w-[40px]">
                                    <div className="flex items-center">
                                        <Checkbox
                                            checked={visible.length > 0 && selected.size === visible.length}
                                            onCheckedChange={toggleAll}
                                            aria-label="Tümünü seç"
                                            data-testid="select-all-products"
                                        />
                                    </div>
                                </th>
                                <SortableTh label="Ürün" sortKey="name" state={sortState} onChange={setSortState} />
                                <SortableTh label="Kalıp" sortKey="fit" state={sortState} onChange={setSortState} presenceMode />
                                <SortableTh label="Model" sortKey="model" state={sortState} onChange={setSortState} presenceMode />
                                <SortableTh label="Görsel" sortKey="image" state={sortState} onChange={setSortState} presenceMode />
                                <SortableTh label="Beden Tablosu" sortKey="table" state={sortState} onChange={setSortState} presenceMode />
                                <Th className="w-[130px] text-center">Durum</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((p) => {
                                const tbl = links[p.id] ? findTable(tables, links[p.id]) : null;
                                const assigned = modelForProduct(models, p.id, products);
                                const fitCopy = p.fit ? getFitCopy(fitLabels, p.fit) : null;
                                const productImage = p.sizingImageId
                                    ? images.find((img) => img.id === p.sizingImageId)
                                    : null;
                                return (
                                    <tr key={p.id} className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors ${selected.has(p.id) ? "bg-muted/30" : ""} ${p.archived ? "opacity-70" : ""}`}>
                                        <td className="px-4 py-0 w-[40px] align-middle">
                                            <div className="flex items-center h-full min-h-[56px]">
                                                <Checkbox
                                                    checked={selected.has(p.id)}
                                                    onCheckedChange={() => toggle(p.id)}
                                                    aria-label={`${p.id} seç`}
                                                    data-testid={`select-${p.id}`}
                                                />
                                            </div>
                                        </td>
                                        {/* Ürün cell — thumbnail + (SKU - Ad) + kategori path (truncate + title). */}
                                        <Td className="align-middle">
                                            <div className="flex items-center gap-3">
                                                <div className="h-10 w-10 rounded-md border border-border bg-muted overflow-hidden shrink-0">
                                                    {p.image ? <img src={p.image} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-4 w-4 text-muted-foreground m-3" />}
                                                </div>
                                                <div className="min-w-0 max-w-[320px]">
                                                    <div className="font-medium truncate" title={`${p.id} - ${p.name || "İsimsiz ürün"}`}>
                                                        <span className="font-mono num text-[11.5px] text-muted-foreground/90">{p.id}</span>
                                                        <span className="text-muted-foreground/60"> - </span>
                                                        {p.name || <span className="italic text-muted-foreground/70">İsimsiz ürün</span>}
                                                    </div>
                                                    {p.category && (
                                                        <div
                                                            className="text-[10.5px] text-muted-foreground truncate"
                                                            title={p.category}
                                                            data-testid={`category-path-${p.id}`}
                                                        >
                                                            {p.category}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </Td>
                                        {/* Kalıp cell — click to open fit dialog; empty placeholder is the sole click target. */}
                                        <Td className="align-middle">
                                            {fitCopy ? (
                                                <button
                                                    onClick={() => setFitTarget(p)}
                                                    className="inline-flex items-center gap-1.5 text-xs hover:text-brand transition-colors text-left group/cell"
                                                    data-testid={`fit-cell-${p.id}`}
                                                    title="Kalıp bilgisini düzenle"
                                                >
                                                    <Shirt className="h-3 w-3 text-muted-foreground shrink-0" strokeWidth={1.75} />
                                                    <span className="font-medium">{fitCopy.label}</span>
                                                    <ChevronRight className="h-3 w-3 text-muted-foreground/40 group-hover/cell:text-brand transition-colors shrink-0" strokeWidth={2} />
                                                </button>
                                            ) : (
                                                <AssignPlaceholder
                                                    icon={Shirt}
                                                    label="Kalıp ata"
                                                    onClick={() => setFitTarget(p)}
                                                    testId={`fit-assign-${p.id}`}
                                                />
                                            )}
                                        </Td>
                                        {/* Model cell — click to open model dialog. */}
                                        <Td className="align-middle">
                                            {assigned ? (
                                                <button
                                                    onClick={() => setModelTarget(p)}
                                                    className="inline-flex items-center gap-1.5 text-xs hover:text-brand transition-colors text-left group/cell"
                                                    data-testid={`model-cell-${p.id}`}
                                                    title="Model atamasını düzenle"
                                                >
                                                    <UserRound className="h-3 w-3 text-muted-foreground shrink-0" strokeWidth={1.75} />
                                                    <span>
                                                        <span className="font-medium">{assigned.model.name}</span>
                                                        {assigned.size && (
                                                            <span className="text-muted-foreground"> · {assigned.size}</span>
                                                        )}
                                                    </span>
                                                    <ChevronRight className="h-3 w-3 text-muted-foreground/40 group-hover/cell:text-brand transition-colors shrink-0" strokeWidth={2} />
                                                </button>
                                            ) : (
                                                <AssignPlaceholder
                                                    icon={UserRound}
                                                    label="Model ata"
                                                    onClick={() => setModelTarget(p)}
                                                    testId={`model-assign-${p.id}`}
                                                />
                                            )}
                                        </Td>
                                        {/* Görsel cell — sizing-chart image from the DAM library. */}
                                        <Td className="align-middle">
                                            {productImage ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            onClick={() => setImageTarget(p)}
                                                            className="inline-flex items-center gap-2 text-xs hover:text-brand transition-colors text-left group/cell"
                                                            data-testid={`sizing-image-cell-${p.id}`}
                                                        >
                                                            <div className="h-8 w-8 rounded-md border border-border bg-muted/40 overflow-hidden shrink-0 p-1">
                                                                <img
                                                                    src={`${process.env.REACT_APP_BACKEND_URL}/api/embed/images/${productImage.id}`}
                                                                    alt=""
                                                                    className="block w-full h-full object-contain"
                                                                />
                                                            </div>
                                                            <span className="font-medium truncate max-w-[100px]">{productImage.name || "Görsel"}</span>
                                                            <ChevronRight className="h-3 w-3 text-muted-foreground/40 group-hover/cell:text-brand transition-colors shrink-0" strokeWidth={2} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    {/* Larger preview on hover so the merchant can eyeball the
                                                        chart without opening the picker dialog. */}
                                                    <TooltipContent
                                                        side="right"
                                                        sideOffset={10}
                                                        className="p-1 bg-popover border border-border rounded-lg shadow-xl max-w-[220px]"
                                                        data-testid={`sizing-image-preview-${p.id}`}
                                                    >
                                                        <div className="rounded-md overflow-hidden bg-muted">
                                                            <img
                                                                src={`${process.env.REACT_APP_BACKEND_URL}/api/embed/images/${productImage.id}`}
                                                                alt={productImage.name || ""}
                                                                className="block w-[200px] h-[200px] object-contain"
                                                            />
                                                        </div>
                                                        {productImage.name && (
                                                            <div className="px-1.5 py-1 text-[10.5px] text-foreground/80 truncate">{productImage.name}</div>
                                                        )}
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                <AssignPlaceholder
                                                    icon={ImageIcon}
                                                    label="Görsel ata"
                                                    onClick={() => setImageTarget(p)}
                                                    testId={`sizing-image-assign-${p.id}`}
                                                />
                                            )}
                                        </Td>
                                        {/* Beden Tablosu cell — icon + table name only (no subtitle). Row's default action.
                                            Exempt products get a subtle "Muaf" chip that reopens the wizard so
                                            the merchant can toggle exemption off. */}
                                        <Td className="align-middle">
                                            {tbl ? (
                                                <button
                                                    onClick={() => onOpenTable(tbl, p)}
                                                    className="inline-flex items-center gap-1.5 text-xs hover:text-brand transition-colors group text-left"
                                                    data-testid={`table-cell-${p.id}`}
                                                    title="Beden tablosunu düzenle"
                                                >
                                                    <Table2 className="h-3 w-3 text-muted-foreground group-hover:text-brand shrink-0" strokeWidth={1.75} />
                                                    <span className="font-medium truncate max-w-[220px]">{tbl.name}</span>
                                                    {tbl.active === false && (
                                                        <span className="text-[10px] font-medium text-muted-foreground/70 uppercase tracking-wide" data-testid={`passive-hint-${p.id}`}>
                                                            · Pasif
                                                        </span>
                                                    )}
                                                    <ChevronRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-brand transition-colors shrink-0" strokeWidth={2} />
                                                </button>
                                            ) : p.sizingTableExempt ? (
                                                <button
                                                    onClick={() => onAddTable(p)}
                                                    className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-border bg-muted/40 text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors text-[11px] group/cell"
                                                    data-testid={`exempt-chip-${p.id}`}
                                                    title="Bu ürün muaf — değiştirmek için tıkla"
                                                >
                                                    <CircleSlash className="h-3 w-3 shrink-0" strokeWidth={1.75} />
                                                    <span>Muaf</span>
                                                    <ChevronRight className="h-3 w-3 text-muted-foreground/40 group-hover/cell:text-foreground transition-colors shrink-0" strokeWidth={2} />
                                                </button>
                                            ) : (
                                                <AssignPlaceholder
                                                    icon={Table2}
                                                    label="Beden tablosu ekle"
                                                    onClick={() => onAddTable(p)}
                                                    testId={`add-table-${p.id}`}
                                                />
                                            )}
                                        </Td>
                                        {/* Durum cell — Aktif/Pasif toggle. Pasif ürünler widget'a servis edilmez
                                            ve XML güncellemesinde otomatik aktife dönmez (manuel kilit). */}
                                        <Td className="text-center align-middle">
                                            <ProductStatusToggle
                                                product={p}
                                                onChange={(next) => askStatusToggle(p, next)}
                                            />
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    )}
                </div>
            </div>

            {/* Footer bar — lives OUTSIDE the table container. Pagination
                controls dock on the right, N–M / T range on the left.
                Selection persists across pages (per merchant expectation). */}
            <ProductsPagination
                page={page}
                limit={limit}
                total={total}
                visibleCount={visible.length}
                onPageChange={setPage}
                onLimitChange={(n) => { setLimit(n); setPage(1); }}
            />

            {modelTarget && <ProductModelDialog product={modelTarget} onClose={() => setModelTarget(null)} />}
            {fitTarget && <ProductFitDialog product={fitTarget} onClose={() => setFitTarget(null)} />}
            {imageTarget && <ProductSizingImageDialog product={imageTarget} onClose={() => setImageTarget(null)} />}

            {/* Aktif → Pasif confirmation (Beden Tabloları'ndaki toggle akışıyla aynı).
                Aktife dönüş direkt uygulanır (geri döndürülebilir).  */}
            <AlertDialog open={!!statusConfirm} onOpenChange={(v) => !v && setStatusConfirm(null)}>
                <AlertDialogContent data-testid="product-status-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Bu ürünü pasife almak istediğine emin misin?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-xs leading-relaxed">
                                <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                                    <div className="font-mono text-[11px] text-muted-foreground">{statusConfirm?.product?.id}</div>
                                    <div className="font-medium text-foreground truncate">{statusConfirm?.product?.name || "İsimsiz ürün"}</div>
                                </div>
                                <p>
                                    Pasif olan ürünlerde widget müşteri sitesinde <strong>görünmez</strong>. Ürün panelde durur — tablo ataması, model ve fit ayarları korunur.
                                    XML güncellemesinde de <strong>otomatik olarak aktife dönmez</strong>; kilitli sayılır.
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="product-status-confirm-cancel">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                const p = statusConfirm?.product;
                                setStatusConfirm(null);
                                if (p) commitStatusToggle(p, false);
                            }}
                            data-testid="product-status-confirm-ok"
                        >
                            Pasife Al
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Bulk action bar & dialogs — surfaced only when the merchant
                selects 2+ rows.  Selection persists after every bulk action
                (per product requirement) so a merchant can do Kalıp → Model →
                Görsel in sequence on the same set. */}
            {selected.size >= 2 && (
                <BulkActionBar
                    selectedCount={selected.size}
                    totalCount={visible.length}
                    onSelectAll={() => setSelected(new Set(visible.map((p) => p.id)))}
                    onClear={clearSelection}
                    onKalip={() => setBulkFitOpen(true)}
                    onModel={() => setBulkModelOpen(true)}
                    onGorsel={() => setBulkImageOpen(true)}
                    onLinkTable={() => setBulkLinkTableOpen(true)}
                    onCreateTable={() => setBulkCreateOpen(true)}
                    anyExempt={anyExempt}
                    allExempt={allExempt}
                    onMarkExempt={bulkMarkExempt}
                    onUnmarkExempt={bulkUnmarkExempt}
                />
            )}
            {bulkFitOpen && (
                <BulkFitDialog
                    selectedProducts={selectedProducts}
                    onClose={() => setBulkFitOpen(false)}
                />
            )}
            {bulkModelOpen && (
                <BulkModelDialog
                    selectedProducts={selectedProducts}
                    onClose={() => setBulkModelOpen(false)}
                />
            )}
            {bulkImageOpen && (
                <BulkImageDialog
                    selectedProducts={selectedProducts}
                    onClose={() => setBulkImageOpen(false)}
                />
            )}
            {bulkLinkTableOpen && (
                <BulkLinkTableDialog
                    selectedProducts={selectedProducts}
                    links={links}
                    onClose={() => setBulkLinkTableOpen(false)}
                />
            )}
            {/* Bulk create — mount a dedicated standalone wizard and seed it
                with the current selection. Wizard already supports N-product
                linking; we just skip the choice screen (standalone) and
                pre-populate `pickedProductIds`. */}
            <AddTableWizard
                standalone={true}
                initialPickedIds={bulkCreateOpen ? selected : null}
                open={bulkCreateOpen}
                onClose={() => setBulkCreateOpen(false)}
            />

            {/* Muaf mark SAFETY confirmation — surfaced only when at least
                one selected product carries a table link that would be
                cleared server-side. Lists every affected ürün → tablo pair
                so the merchant sees exactly what disappears before consent. */}
            <AlertDialog
                open={!!muafConfirm}
                onOpenChange={(o) => !o && setMuafConfirm(null)}
            >
                <AlertDialogContent data-testid="bulk-muaf-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Muaf işaretle · Tablo bağlantıları kaldırılacak
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Seçtiğin{" "}
                            <span className="font-medium text-foreground">{muafConfirm?.targets.length}</span>{" "}
                            ürünün{" "}
                            <span className="font-medium text-warning">{muafConfirm?.withTable.length}</span>
                            &apos;i şu an bir beden tablosuna bağlı. Muaf yaparsan bu bağlantılar kaldırılır — <span className="text-foreground">tabloların kendisi silinmez</span>, sadece bu ürünlerle olan bağı gider.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {muafConfirm && (
                        <div className="max-h-56 overflow-y-auto scroll-thin rounded-md border border-border bg-muted/40" data-testid="bulk-muaf-affected-list">
                            {muafConfirm.withTable.map((p) => {
                                const tbl = findTable(tables, links[p.id]);
                                return (
                                    <div
                                        key={p.id}
                                        className="flex items-center gap-2 px-3 py-2 text-xs border-b border-border last:border-0"
                                        data-testid={`bulk-muaf-affected-${p.id}`}
                                    >
                                        <span className="font-mono text-[10.5px] text-muted-foreground shrink-0">{p.id}</span>
                                        <span className="truncate flex-1 font-medium">{p.name || <span className="italic text-muted-foreground">İsim yok</span>}</span>
                                        <span className="text-muted-foreground/60 shrink-0">→</span>
                                        <span className="text-warning truncate max-w-[160px] shrink-0" title={tbl?.name}>{tbl?.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="bulk-muaf-cancel">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (muafConfirm) applyBulkMuafMark(muafConfirm.targets);
                                setMuafConfirm(null);
                            }}
                            data-testid="bulk-muaf-confirm-action"
                        >
                            Muaf yap, bağları kaldır
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// ----------------- Tables view -----------------
//
// Design principles for this page (locked, per user directive 2026-02-27):
//   • Header CTA "Yeni Beden Tablosu" lives in the PageHeader (`Tables()` above).
//     The toolbar therefore does NOT duplicate a "Yeni Tablo" button.
//   • Draft/Published/Awaiting badges are gone. Status is expressed by a single
//     `active` boolean per table. Pasif = kill switch for the widget on-site,
//     regardless of product links.
//   • Filters (Series, Group, Products, Active/Passive) are multi-select
//     popovers driven by ACTUAL data — no free-text inputs. All filters
//     compose (AND semantics) with the search box.
//   • Bulk actions surface only when 1+ tables are checkbox-selected.
//   • Toggling ACTIVE → PASSIVE opens an INLINE micro-confirmation on the
//     row itself (no modal). The confirmation includes the count of bound
//     products so the merchant knows the blast radius.
//   • Deactivating is a two-step commit; re-activating is a single click.

/**
 * Blast-radius aware confirmation for bulk actions on tables.
 *
 * Semantics per action:
 * • activate   — no risk; single "N tabloyu aktifleştir" confirmation.
 * • passivate  — reversible; splits the set into "safe" (0 bound products)
 *                and "linked" (>0). If linked > 0, offers two choices:
 *                "Hepsini pasife al" or "Sadece ürüne bağlı olmayanları pasife al".
 * • delete     — irreversible + cascades product unlinks. Same choice split,
 *                plus a mandatory ack checkbox before the destructive button
 *                un-disables.
 */
const BulkConfirmDialog = ({
    confirmBulk, setConfirmBulk,
    bulkScope, setBulkScope,
    ackBulkDelete, setAckBulkDelete,
    tables, productsByTable, onCommit,
}) => {
    if (!confirmBulk) return null;
    const { action, ids } = confirmBulk;
    const rows = ids
        .map((id) => tables.find((t) => t.id === id))
        .filter(Boolean);
    const linked = rows.filter((t) => (productsByTable.get(t.id)?.size || 0) > 0);
    const safe = rows.filter((t) => (productsByTable.get(t.id)?.size || 0) === 0);
    const hasChoice = (action !== "activate") && linked.length > 0 && safe.length > 0;
    const targetIds = (hasChoice && bulkScope === "safe")
        ? safe.map((t) => t.id)
        : rows.map((t) => t.id);

    const isDelete = action === "delete";
    const isActivate = action === "activate";

    const title = isActivate
        ? `${rows.length} tabloyu aktifleştir?`
        : isDelete
            ? `${rows.length} tabloyu sil?`
            : `${rows.length} tabloyu pasife al?`;

    const primaryLabel = isActivate ? "Aktifleştir" : isDelete ? "Evet, sil" : "Pasife Al";
    const primaryClass = isDelete
        ? "bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:pointer-events-none"
        : "";
    const disabledPrimary =
        (targetIds.length === 0) ||
        (isDelete && linked.length > 0 && bulkScope === "all" && !ackBulkDelete);

    return (
        <AlertDialog open={!!confirmBulk} onOpenChange={(v) => { if (!v) { setConfirmBulk(null); setBulkScope("all"); setAckBulkDelete(false); } }}>
            <AlertDialogContent data-testid="bulk-confirm-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {isActivate && "Seçili tablolar aktif duruma alınacak — bağlı oldukları ürünlerde widget yeniden gösterilmeye başlar."}
                        {!isActivate && !isDelete && (
                            <>
                                {linked.length === 0 && "Seçili tabloların hiçbiri ürünle bağlı değil — widget gösterimi doğrudan durur."}
                                {linked.length > 0 && safe.length === 0 && (
                                    <>Seçilen <span className="font-medium text-foreground">{rows.length} tablonun hepsi</span> en az bir ürünle bağlı. Pasife alınca bu ürünlerde widget artık gösterilmeyecek. Ürün bağlantıları korunur.</>
                                )}
                                {linked.length > 0 && safe.length > 0 && (
                                    <>Seçilen <span className="font-medium text-foreground">{rows.length} tablodan {linked.length} tanesi</span> en az bir ürünle bağlı. Nasıl devam edeyim?</>
                                )}
                            </>
                        )}
                        {isDelete && (
                            <>
                                {linked.length === 0 && "Seçili tabloların hiçbiri ürünle bağlı değil — silme işlemi güvenli."}
                                {linked.length > 0 && safe.length === 0 && (
                                    <>Seçilen <span className="font-medium text-foreground">{rows.length} tablonun hepsi</span> ürünle bağlı. Silersen bu ürünler beden tablosuz kalır ve widget gösterilmez. Bu işlem geri alınamaz.</>
                                )}
                                {linked.length > 0 && safe.length > 0 && (
                                    <>Seçilen <span className="font-medium text-foreground">{rows.length} tablodan {linked.length} tanesi</span> ürünle bağlı. Silmek geri alınamaz — bağlı ürünler tablosuz kalır. Nasıl devam edeyim?</>
                                )}
                            </>
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>

                {hasChoice && (
                    <div className="space-y-2" data-testid="bulk-scope-choice">
                        <label className={`flex items-start gap-2.5 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${bulkScope === "all" ? "border-foreground bg-muted/40" : "border-border hover:border-foreground/40"}`}>
                            <input
                                type="radio"
                                name="bulk-scope"
                                value="all"
                                checked={bulkScope === "all"}
                                onChange={() => setBulkScope("all")}
                                className="mt-0.5"
                                data-testid="bulk-scope-all"
                            />
                            <div className="text-xs leading-relaxed">
                                <div className="font-medium text-foreground">
                                    {isDelete ? `Hepsini sil (${rows.length})` : `Hepsini pasife al (${rows.length})`}
                                </div>
                                <div className="text-muted-foreground mt-0.5">
                                    Ürüne bağlı {linked.length} tablo dahil hepsi işleme alınır.
                                </div>
                            </div>
                        </label>
                        <label className={`flex items-start gap-2.5 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${bulkScope === "safe" ? "border-foreground bg-muted/40" : "border-border hover:border-foreground/40"}`}>
                            <input
                                type="radio"
                                name="bulk-scope"
                                value="safe"
                                checked={bulkScope === "safe"}
                                onChange={() => setBulkScope("safe")}
                                className="mt-0.5"
                                data-testid="bulk-scope-safe"
                            />
                            <div className="text-xs leading-relaxed">
                                <div className="font-medium text-foreground">
                                    Sadece ürüne bağlı olmayanlar ({safe.length})
                                </div>
                                <div className="text-muted-foreground mt-0.5">
                                    Ürüne bağlı {linked.length} tablo bu işlemden hariç tutulur.
                                </div>
                            </div>
                        </label>
                    </div>
                )}

                {isDelete && linked.length > 0 && bulkScope === "all" && (
                    <label className="flex items-start gap-2.5 rounded-md border border-warning/30 bg-warning-soft/50 px-3 py-2.5 cursor-pointer" data-testid="bulk-delete-ack-row">
                        <Checkbox
                            checked={ackBulkDelete}
                            onCheckedChange={(v) => setAckBulkDelete(!!v)}
                            className="mt-0.5"
                            data-testid="bulk-delete-ack"
                        />
                        <span className="text-xs text-foreground leading-relaxed">
                            {linked.length} tabloya bağlı ürünlerin beden tablosuz kalacağını anladım.
                        </span>
                    </label>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="bulk-confirm-cancel">Vazgeç</AlertDialogCancel>
                    <AlertDialogAction
                        disabled={disabledPrimary}
                        onClick={() => onCommit(targetIds, action)}
                        className={primaryClass}
                        data-testid="bulk-confirm-action"
                    >
                        {primaryLabel} ({targetIds.length})
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};


/**
 * AssignmentFilterDropdown — "Atama Durumu" filtresi (Tümü / Bağlı / Bağlı
 * Değil). Beden Tabloları toolbar'ında kullanılır; tablonun ürünlere atanıp
 * atanmadığını filtreler. UI pattern olarak MultiSelectFilterPopover /
 * PresenceFilterPopover trigger görünümüyle hizalı — küçük bir dropdown
 * butonu + 3 radio seçim.
 */
const AssignmentFilterDropdown = ({ value, onChange }) => {
    const active = value && value !== "all";
    const triggerBadge = value === "assigned" ? "Bağlı" : value === "unassigned" ? "Bağlı Değil" : null;
    const options = [
        { key: "all", label: "Tümü" },
        { key: "assigned", label: "Bağlı" },
        { key: "unassigned", label: "Bağlı Değil" },
    ];
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`h-9 inline-flex items-center gap-1.5 rounded-md border px-3 text-xs transition-colors ${active ? "border-foreground/40 bg-muted/60 text-foreground" : "border-border bg-surface text-muted-foreground hover:text-foreground"}`}
                    data-testid="filter-assignment"
                >
                    <Filter className="h-3 w-3" />
                    <span>Atama Durumu</span>
                    {triggerBadge && (
                        <span className="rounded-md bg-brand text-brand-fg px-1.5 py-0.5 text-[10px] font-medium">{triggerBadge}</span>
                    )}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-56 p-1 overflow-hidden">
                <div role="radiogroup" aria-label="Atama durumu filtresi">
                    {options.map((opt) => {
                        const selected = value === opt.key;
                        return (
                            <button
                                key={opt.key}
                                role="radio"
                                aria-checked={selected}
                                onClick={() => onChange(opt.key)}
                                className={`w-full text-left flex items-center gap-2 px-2.5 py-2 text-xs rounded-md transition-colors ${selected ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"}`}
                                data-testid={`filter-assignment-${opt.key}`}
                            >
                                <span className={`h-3.5 w-3.5 rounded-full border grid place-items-center shrink-0 ${selected ? "border-foreground" : "border-border"}`}>
                                    {selected && <span className="h-1.5 w-1.5 rounded-full bg-foreground" />}
                                </span>
                                <span className="flex-1">{opt.label}</span>
                            </button>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
};


const TablesView = ({ onOpen }) => {
    const { tables, links, products, series, groups, setTableActive, removeTable } = useSizing();
    const [searchParams, setSearchParams] = useSearchParams();

    const statusFromUrl = () => {
        const v = (searchParams.get("statusFilter") || "all").toLowerCase();
        return ["all", "active", "passive"].includes(v) ? v : "all";
    };
    const assignmentFromUrl = () => {
        const v = (searchParams.get("assignment") || "all").toLowerCase();
        return ["all", "assigned", "unassigned"].includes(v) ? v : "all";
    };

    // Filter state — all AND-composed.
    const [search, setSearch] = useState("");
    const [seriesFilter, setSeriesFilter] = useState([]);
    const [groupFilter, setGroupFilter] = useState([]);
    const [productFilter, setProductFilter] = useState([]);
    const [statusFilter, setStatusFilter] = useState(statusFromUrl); // all | active | passive
    const [assignmentFilter, setAssignmentFilter] = useState(assignmentFromUrl); // all | assigned | unassigned

    // URL ↔ state sync (statusFilter + assignmentFilter).
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (!statusFilter || statusFilter === "all") next.delete("statusFilter");
        else next.set("statusFilter", statusFilter);
        if (!assignmentFilter || assignmentFilter === "all") next.delete("assignment");
        else next.set("assignment", assignmentFilter);
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, assignmentFilter]);

    // Bulk selection
    const [selected, setSelected] = useState(new Set());

    // AlertDialog: "activate → passive" confirmation. Stores the row being paused.
    // Passive is a widget kill switch only; product links are always preserved.
    const [confirmPassive, setConfirmPassive] = useState(null); // {id, name} | null
    // AlertDialog: destructive delete confirmation. Stores the row being deleted.
    // The shared DeleteConfirmDialog handles its own ack state internally.
    const [confirmDelete, setConfirmDelete] = useState(null); // {id, name, count} | null

    // Bulk operation confirmation dialogs. All three (activate / passivate /
    // delete) open a modal that surfaces the "linked products" blast radius
    // and lets the merchant pick the safe subset when applicable.
    const [confirmBulk, setConfirmBulk] = useState(null);
    // Shape: { action: "activate" | "passivate" | "delete", ids: string[] }
    const [bulkScope, setBulkScope] = useState("all"); // "all" | "safe"
    const [ackBulkDelete, setAckBulkDelete] = useState(false);

    // Product → tableId map for the "Bağlı ürünler" filter.
    const productsByTable = useMemo(() => {
        const map = new Map();
        Object.entries(links).forEach(([pid, tid]) => {
            if (!map.has(tid)) map.set(tid, new Set());
            map.get(tid).add(pid);
        });
        return map;
    }, [links]);

    const seriesById = useMemo(() => new Map(series.map((s) => [s.id, s])), [series]);
    const groupsById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

    // Compose all filters. Result is sorted by last-edited DESC so the most
    // recently updated table always sits on top of the list.
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = tables.filter((t) => {
            if (q && !t.name.toLowerCase().includes(q)) return false;
            if (seriesFilter.length && !seriesFilter.includes(t.seriesLabel)) return false;
            if (groupFilter.length && !groupFilter.includes(t.groupLabel)) return false;
            if (productFilter.length) {
                const linked = productsByTable.get(t.id) || new Set();
                if (!productFilter.some((pid) => linked.has(pid))) return false;
            }
            if (statusFilter === "active" && t.active === false) return false;
            if (statusFilter === "passive" && t.active !== false) return false;
            if (assignmentFilter !== "all") {
                const linkedCount = productsByTable.get(t.id)?.size || 0;
                if (assignmentFilter === "assigned" && linkedCount === 0) return false;
                if (assignmentFilter === "unassigned" && linkedCount > 0) return false;
            }
            return true;
        });
        const ts = (t) => {
            const v = t.updated ?? t.createdAt ?? 0;
            const n = typeof v === "number" ? v : Date.parse(v);
            return Number.isFinite(n) ? n : 0;
        };
        return list.sort((a, b) => ts(b) - ts(a));
    }, [tables, search, seriesFilter, groupFilter, productFilter, statusFilter, assignmentFilter, productsByTable]);

    const hasAnyFilter = search || seriesFilter.length || groupFilter.length || productFilter.length || statusFilter !== "all" || assignmentFilter !== "all";
    const clearAll = () => {
        setSearch(""); setSeriesFilter([]); setGroupFilter([]); setProductFilter([]); setStatusFilter("all"); setAssignmentFilter("all");
    };

    // Bulk-selection helpers.
    const allChecked = filtered.length > 0 && filtered.every((t) => selected.has(t.id));
    const someChecked = filtered.some((t) => selected.has(t.id));
    const toggleAll = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allChecked) filtered.forEach((t) => next.delete(t.id));
            else filtered.forEach((t) => next.add(t.id));
            return next;
        });
    };
    const toggleOne = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    // Bulk actions — every action opens a confirmation dialog. This gives the
    // merchant a chance to review the blast radius (linked products) before
    // making changes across many tables at once.
    const openBulkConfirm = (action) => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        setBulkScope("all");
        setAckBulkDelete(false);
        setConfirmBulk({ action, ids });
    };
    // Per-row active toggle. Turning passive → active is one-click and does NOT
    // bump `updated` (setTableActive) so the row stays where it is in the list.
    // Turning active → passive opens an AlertDialog to reinforce the mental model
    // that passive is a widget-visibility toggle, NOT a product unlink.
    const handleToggleActive = (t) => {
        if (t.active === false) {
            setTableActive(t.id, true);
            toast.success(`"${t.name}" aktifleştirildi`);
            return;
        }
        setConfirmPassive({ id: t.id, name: t.name });
    };
    const commitPassive = () => {
        if (!confirmPassive) return;
        setTableActive(confirmPassive.id, false);
        toast.success(`"${confirmPassive.name}" pasife alındı`);
        setConfirmPassive(null);
    };
    const commitDelete = () => {
        if (!confirmDelete) return;
        removeTable(confirmDelete.id);
        setSelected((prev) => {
            const next = new Set(prev);
            next.delete(confirmDelete.id);
            return next;
        });
        const cnt = confirmDelete.count || 0;
        toast.success(`"${confirmDelete.name}" silindi${cnt > 0 ? ` · ${cnt} ürün tablosuz kaldı` : ""}`);
        setConfirmDelete(null);
    };

    // Distinct series/group labels for filter options (some tables may reference labels
    // that no longer exist as series/groups; we surface exactly what's in use).
    const seriesOptions = useMemo(() => {
        const seen = new Set();
        return tables
            .map((t) => t.seriesLabel)
            .filter((lbl) => { if (!lbl || seen.has(lbl)) return false; seen.add(lbl); return true; })
            .map((lbl) => ({ id: lbl, name: lbl }));
    }, [tables]);
    const groupOptions = useMemo(() => {
        const seen = new Set();
        return tables
            .map((t) => t.groupLabel)
            .filter((lbl) => { if (!lbl || seen.has(lbl)) return false; seen.add(lbl); return true; })
            .map((lbl) => ({ id: lbl, name: lbl }));
    }, [tables]);

    return (
        <div className="rounded-xl border border-border bg-surface overflow-hidden" data-testid="tables-view">
            {/* Toolbar — search grows to fill; filter items hug the right edge. */}
            <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[280px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Tablo adına göre ara…"
                        className="h-9 pl-9 text-xs bg-background w-full"
                        data-testid="tables-search"
                    />
                </div>
                <MultiSelectFilterPopover
                    label="Seri"
                    options={seriesOptions}
                    selected={seriesFilter}
                    onChange={setSeriesFilter}
                    keyOf={(o) => o.id}
                    labelOf={(o) => o.name}
                    emptyLabel="Kayıtlı seri yok"
                    testId="filter-series"
                />
                <MultiSelectFilterPopover
                    label="Ölçü Grubu"
                    options={groupOptions}
                    selected={groupFilter}
                    onChange={setGroupFilter}
                    keyOf={(o) => o.id}
                    labelOf={(o) => o.name}
                    emptyLabel="Kayıtlı grup yok"
                    testId="filter-group"
                />
                <MultiSelectFilterPopover
                    label="Bağlı Ürün"
                    options={products}
                    selected={productFilter}
                    onChange={setProductFilter}
                    keyOf={(o) => o.id}
                    labelOf={(o) => o.name || o.id}
                    searchOf={(o) => `${o.id || ""} ${o.name || ""}`}
                    renderOption={(o) => (
                        <span className="min-w-0 flex-1 flex items-center gap-2 truncate">
                            <span className="text-[10.5px] font-mono text-muted-foreground shrink-0">{o.id}</span>
                            <span className="text-muted-foreground/50 shrink-0">—</span>
                            <span className="truncate">{o.name || <span className="text-muted-foreground italic">İsim yok</span>}</span>
                        </span>
                    )}
                    emptyLabel="Kayıtlı ürün yok"
                    testId="filter-product"
                />
                <AssignmentFilterDropdown value={assignmentFilter} onChange={setAssignmentFilter} />
                <div className="inline-flex rounded-md border border-border p-0.5 bg-surface" data-testid="filter-status">
                    {[
                        { key: "all", label: "Tümü" },
                        { key: "active", label: "Aktif" },
                        { key: "passive", label: "Pasif" },
                    ].map((opt) => (
                        <button
                            key={opt.key}
                            onClick={() => setStatusFilter(opt.key)}
                            className={`h-8 px-3 text-[11px] font-medium rounded-sm transition-colors ${statusFilter === opt.key ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                            data-testid={`filter-status-${opt.key}`}
                            aria-pressed={statusFilter === opt.key}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Active filter chips */}
            {hasAnyFilter && (
                <div className="px-4 py-2.5 border-b border-border bg-muted/20 flex items-center gap-1.5 flex-wrap" data-testid="active-filter-chips">
                    {search && (
                        <FilterChip label={`Ara: "${search}"`} onClear={() => setSearch("")} testId="chip-search" />
                    )}
                    {seriesFilter.map((s) => (
                        <FilterChip key={s} label={`Seri: ${s}`} onClear={() => setSeriesFilter(seriesFilter.filter((x) => x !== s))} testId={`chip-series-${s}`} />
                    ))}
                    {groupFilter.map((g) => (
                        <FilterChip key={g} label={`Grup: ${g}`} onClear={() => setGroupFilter(groupFilter.filter((x) => x !== g))} testId={`chip-group-${g}`} />
                    ))}
                    {productFilter.map((pid) => {
                        const p = products.find((x) => x.id === pid);
                        return (
                            <FilterChip key={pid} label={`Ürün: ${p?.name || pid}`} onClear={() => setProductFilter(productFilter.filter((x) => x !== pid))} testId={`chip-product-${pid}`} />
                        );
                    })}
                    {statusFilter !== "all" && (
                        <FilterChip label={`Durum: ${statusFilter === "active" ? "Aktif" : "Pasif"}`} onClear={() => setStatusFilter("all")} testId="chip-status" />
                    )}
                    {assignmentFilter !== "all" && (
                        <FilterChip
                            label={`Atama: ${assignmentFilter === "assigned" ? "Bağlı" : "Bağlı Değil"}`}
                            onClear={() => setAssignmentFilter("all")}
                            testId="chip-assignment"
                        />
                    )}
                    <button
                        onClick={clearAll}
                        className="ml-auto text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                        data-testid="clear-all-filters"
                    >
                        Tümünü temizle
                    </button>
                </div>
            )}

            {/* Bulk action bar (visible when 1+ selected). Every action opens
                a confirmation dialog — no destructive change happens without
                the merchant seeing the blast radius. */}
            {selected.size > 0 && (
                <div className="px-4 py-2.5 border-b border-border bg-brand/5 flex items-center gap-3 text-xs" data-testid="bulk-actions">
                    <span className="font-medium text-foreground">{selected.size} tablo seçili</span>
                    <div className="h-4 w-px bg-border" />
                    <button
                        onClick={() => openBulkConfirm("activate")}
                        className="text-xs text-foreground hover:text-brand transition-colors"
                        data-testid="bulk-activate"
                    >
                        Toplu Aktif
                    </button>
                    <button
                        onClick={() => openBulkConfirm("passivate")}
                        className="text-xs text-foreground hover:text-brand transition-colors"
                        data-testid="bulk-passivate"
                    >
                        Toplu Pasif
                    </button>
                    <button
                        onClick={() => openBulkConfirm("delete")}
                        className="text-xs text-destructive hover:text-destructive/80 transition-colors"
                        data-testid="bulk-delete"
                    >
                        Toplu Sil
                    </button>
                    <button
                        onClick={() => setSelected(new Set())}
                        className="ml-auto text-[11px] text-muted-foreground hover:text-foreground"
                        data-testid="bulk-cancel"
                    >
                        Vazgeç
                    </button>
                </div>
            )}

            {/* Data table */}
            {filtered.length === 0 ? (
                <div className="px-6 py-16 text-center" data-testid="tables-empty">
                    <div className="mx-auto h-12 w-12 rounded-xl border border-border bg-muted/40 grid place-items-center mb-4">
                        <Table2 className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {hasAnyFilter ? "Filtrelere uyan tablo yok." : "Henüz beden tablosu yok."}
                    </p>
                    {hasAnyFilter && (
                        <button
                            onClick={clearAll}
                            className="mt-3 text-xs text-brand hover:underline"
                            data-testid="empty-clear-filters"
                        >
                            Filtreleri temizle
                        </button>
                    )}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tables-table">
                        <thead>
                            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                <Th className="w-[40px] pr-0">
                                    <div className="flex items-center">
                                        <Checkbox
                                            checked={allChecked ? true : someChecked ? "indeterminate" : false}
                                            onCheckedChange={toggleAll}
                                            data-testid="check-all"
                                        />
                                    </div>
                                </Th>
                                <Th>Tablo</Th>
                                <Th>Seri</Th>
                                <Th>Ölçü Grubu</Th>
                                <Th className="text-right">Ürün Sayısı</Th>
                                <Th>Son Düzenleme</Th>
                                <Th className="w-[130px] text-center">Durum</Th>
                                <Th className="w-[90px] text-center">İşlem</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((t) => {
                                const count = productsByTable.get(t.id)?.size || 0;
                                const isActive = t.active !== false;
                                return (
                                    <tr
                                        key={t.id}
                                        className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors ${isActive ? "" : "opacity-70"}`}
                                        data-testid={`table-row-${t.id}`}
                                    >
                                        <Td className="pr-0">
                                            <div className="flex items-center">
                                                <Checkbox
                                                    checked={selected.has(t.id)}
                                                    onCheckedChange={() => toggleOne(t.id)}
                                                    data-testid={`row-check-${t.id}`}
                                                />
                                            </div>
                                        </Td>
                                        <Td>
                                            <button
                                                onClick={() => onOpen(t)}
                                                className="inline-flex items-center gap-2.5 hover:text-brand transition-colors group text-left"
                                                data-testid={`table-open-${t.id}`}
                                            >
                                                <span className="h-8 w-8 rounded-md border border-border bg-muted/40 grid place-items-center shrink-0">
                                                    <Table2 className="h-3.5 w-3.5 text-muted-foreground group-hover:text-brand" strokeWidth={1.75} />
                                                </span>
                                                <span className="font-display font-bold tracking-tight">{t.name}</span>
                                            </button>
                                        </Td>
                                        <Td className="text-xs">{t.seriesLabel ? `${t.seriesLabel} · ${(t.sizes || []).length} beden` : "—"}</Td>
                                        <Td className="text-xs text-muted-foreground">{t.groupLabel ? `${t.groupLabel} · ${(t.measures || []).length} ölçü` : "—"}</Td>
                                        <Td className="text-right font-mono num text-sm">{count}</Td>
                                        <Td className="text-xs text-muted-foreground">{timeAgo(t.updated)}</Td>
                                        <Td className="text-center">
                                            <div className="inline-flex items-center gap-2">
                                                <span className={`text-[10.5px] font-medium ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                                                    {isActive ? "Aktif" : "Pasif"}
                                                </span>
                                                <Switch
                                                    checked={isActive}
                                                    onCheckedChange={() => handleToggleActive(t)}
                                                    data-testid={`row-active-switch-${t.id}`}
                                                />
                                            </div>
                                        </Td>
                                        <Td className="text-center">
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => onOpen(t)}
                                                    className="h-7 w-7 rounded-md grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                                    aria-label="Düzenle"
                                                    title="Düzenle"
                                                    data-testid={`row-edit-${t.id}`}
                                                >
                                                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setConfirmDelete({ id: t.id, name: t.name, count })}
                                                    className="h-7 w-7 rounded-md grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                                    aria-label="Sil"
                                                    title="Sil"
                                                    data-testid={`row-delete-${t.id}`}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                </button>
                                            </div>
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Active → Passive confirmation. Passive is a widget kill switch; product links are preserved. */}
            <AlertDialog open={!!confirmPassive} onOpenChange={(v) => !v && setConfirmPassive(null)}>
                <AlertDialogContent data-testid="confirm-passive-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmPassive ? `"${confirmPassive.name}" tablosunu pasife al?` : "Tabloyu pasife al?"}</AlertDialogTitle>
                        <AlertDialogDescription>
                            Bu tablo widget'ta artık gösterilmeyecek. Ürün bağlantıları korunacak.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-passive-cancel">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction onClick={commitPassive} data-testid="confirm-passive-action">Pasife Al</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Destructive delete confirmation. Uses the shared DeleteConfirmDialog
                so it matches Görsel/Model silme popup UX (trash icon in header,
                amber impact banner, forced ack when in use, red confirm CTA). */}
            <DeleteConfirmDialog
                open={!!confirmDelete}
                onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}
                itemName={confirmDelete?.name}
                impactCount={confirmDelete?.count || 0}
                impactMessage={
                    <>
                        <strong>{confirmDelete?.count || 0} ürün</strong> bu tabloya bağlı. Silersen bu ürünler beden tablosuz kalır ve mağazanda beden tablosu widget&apos;ı gösterilmez.
                    </>
                }
                permanentNote="Tablo ve tüm ölçüm değerleri kalıcı olarak silinir. Bu işlem geri alınamaz."
                ackMessage={<><strong>{confirmDelete?.count || 0} ürün</strong> bu silme sonrası beden tablosuz kalacak.</>}
                onConfirm={commitDelete}
                testId="table-delete"
            />

            {/* Bulk operation confirmation. Surfaces linked-product blast radius
                and, when relevant, lets the merchant choose between "hepsi" (all)
                or "sadece ürüne bağlı olmayanlar" (safe subset). Delete adds an
                ack checkbox because it's irreversible and cascades an unlink. */}
            <BulkConfirmDialog
                confirmBulk={confirmBulk}
                setConfirmBulk={setConfirmBulk}
                bulkScope={bulkScope}
                setBulkScope={setBulkScope}
                ackBulkDelete={ackBulkDelete}
                setAckBulkDelete={setAckBulkDelete}
                tables={tables}
                productsByTable={productsByTable}
                onCommit={(targetIds, action) => {
                    if (action === "activate") {
                        targetIds.forEach((id) => setTableActive(id, true));
                        toast.success(`${targetIds.length} tablo aktifleştirildi`);
                    } else if (action === "passivate") {
                        targetIds.forEach((id) => setTableActive(id, false));
                        toast.success(`${targetIds.length} tablo pasife alındı`);
                    } else if (action === "delete") {
                        targetIds.forEach((id) => removeTable(id));
                        toast.success(`${targetIds.length} tablo silindi`);
                    }
                    setSelected((prev) => {
                        const next = new Set(prev);
                        targetIds.forEach((id) => next.delete(id));
                        return next;
                    });
                    setConfirmBulk(null);
                    setAckBulkDelete(false);
                    setBulkScope("all");
                }}
            />
        </div>
    );
};

// ----------------- Edit existing table -----------------
const EditTableDialog = ({ table: tableProp, fromProduct, open, onClose }) => {
    const { tables, links, products, series, groups, linkProduct, unlinkProduct, removeTable, updateTable } = useSizing();
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmDetach, setConfirmDetach] = useState(false);
    // Ürün bağlama akışı artık ayrı bir modal'da (LinkProductsModal). Drawer
    // içinde yaşayan cramped panel değil — full width & height, product cards
    // with thumbnails.
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkedQuery, setLinkedQuery] = useState("");
    const [pendingRemove, setPendingRemove] = useState(null);
    // Delete confirmations for row (size) / column (measure) — we NEVER remove
    // silently from an existing table because the merchant may have entered cm
    // values that would be lost. Instead we open an AlertDialog that surfaces
    // exactly how many values will disappear.
    const [pendingRowDelete, setPendingRowDelete] = useState(null);   // { idx, name, cmCount }
    const [pendingColDelete, setPendingColDelete] = useState(null);   // { idx, name, cmCount }
    const [renaming, setRenaming] = useState(false);
    const [nameDraft, setNameDraft] = useState("");
    const [values, setValues] = useState({});
    // Local editable copies of sizes/measures so the merchant can add/remove
    // rows and columns without immediately mutating the store. Committed on
    // save alongside `values`.
    const [localSizes, setLocalSizes] = useState([]);
    const [localMeasures, setLocalMeasures] = useState([]);
    // Kind sidecar — the UI drives everything off the string measure name so
    // grid keys stay simple, but the server stores `measures: [{name, kind}]`
    // (kind powers the Find My Size suggester). We cache kinds by name on
    // load and re-attach them on save so the structured format round-trips.
    const [measureKinds, setMeasureKinds] = useState({});
    // Lock the "Kaydet" button while the /tables PUT is in flight to prevent
    // double-submit. Declared at top of component so hook order stays stable
    // across renders (early return below).
    const [saving, setSaving] = useState(false);
    const cellRefs = useRef({});
    // DnD sensors — a small activation distance stops accidental drags when
    // the merchant just wants to click into an input cell.
    const dndSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // Always resolve the freshest table from the store (counts/links are live)
    const table = tableProp ? (findTable(tables, tableProp.id) || tableProp) : null;

    // Opened from a product row => detach action targets that product; from Tables tab => table-level actions.
    const ctxProduct = fromProduct || null;

    useEffect(() => {
        setLinkModalOpen(false);
        setPendingRemove(null);
        setRenaming(false);
        setLinkedQuery("");
        const t = tableProp ? (findTable(tables, tableProp.id) || tableProp) : null;
        // Normalize structured measures `[{name, kind}, ...]` down to a plain
        // string[] for the editor grid, and side-car the kinds so we can
        // round-trip them on save. Legacy string entries are tolerated.
        const rawMeasures = t?.measures || [];
        const measureNames = rawMeasures
            .map((m) => (typeof m === "string" ? m : m?.name))
            .filter(Boolean);
        const kindMap = {};
        rawMeasures.forEach((m) => {
            if (m && typeof m === "object" && m.name && m.kind) kindMap[m.name] = m.kind;
        });
        setValues(t ? valueMapToGrid(t.values, t.sizes, measureNames) : {});
        setLocalSizes(t ? [...(t.sizes || [])] : []);
        setLocalMeasures(measureNames);
        setMeasureKinds(kindMap);
    }, [tableProp?.id, fromProduct?.id]);

    if (!table) return null;
    const allLinked = linkedProductsOf(links, table.id, products);
    const count = allLinked.length;
    const isShared = count > 1;
    const lq = linkedQuery.trim().toLowerCase();
    const linkedProducts = lq
        ? allLinked.filter((p) => p.name.toLowerCase().includes(lq) || p.id.toLowerCase().includes(lq))
        : allLinked;

    // Callback that LinkProductsModal fires after committing a bulk-link.
    const handleBulkLinked = ({ linked, moved }) => {
        toast.success(
            moved > 0
                ? `${linked} ürün tabloya bağlandı · ${moved} tanesi başka tablodan taşındı`
                : `${linked} ürün tabloya bağlandı`
        );
        setLinkModalOpen(false);
    };

    const confirmRemoveProduct = () => {
        const p = pendingRemove;
        if (!p) return;
        unlinkProduct(p.id);
        toast.success(`${p.id} tablodan ayrıldı`);
        setPendingRemove(null);
    };

    const saveRename = () => {
        if (!nameDraft.trim()) {
            toast.error("Tablo adı boş olamaz");
            return;
        }
        updateTable(table.id, { name: nameDraft.trim() });
        toast.success(`Tablo adı "${nameDraft.trim()}" olarak güncellendi`);
        setRenaming(false);
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await updateTable(table.id, {
                sizes: localSizes,
                // Re-attach structured kinds; unknown names (added via source
                // group fallback where the kind lookup missed) default to
                // "product" so the DB document stays well-formed.
                measures: localMeasures.map((name) => ({ name, kind: measureKinds[name] || "product" })),
                values: gridToValueMap(values, localSizes, localMeasures),
            });
            toast.success(`${table.name} güncellendi — ölçüler kaydedildi`);
            onClose();
        } catch (_) {
            // Store already toasted + refreshed. Keep the drawer open so user can retry.
        } finally {
            setSaving(false);
        }
    };

    // Source series/group lookup — explicit FK first, fall back to legacy label
    // match for tables created before sourceSeriesId / sourceGroupId shipped.
    const sourceSeries = table
        ? (series.find((s) => s.id === table.sourceSeriesId)
            || series.find((s) => s.name === table.seriesLabel))
        : null;
    const sourceGroup = table
        ? (groups.find((g) => g.id === table.sourceGroupId)
            || groups.find((g) => g.name === table.groupLabel))
        : null;
    // Sizes / measures still available in the source (not yet used in this
    // table). Feeds the "Beden Ekle" / "Ölçü Ekle" popovers below.
    const availableSizes = (sourceSeries?.sizes || []).filter((sz) => !localSizes.includes(sz));
    const availableMeasures = (sourceGroup?.measures || []).map((m) => m.name || m).filter((n) => n && !localMeasures.includes(n));

    // Grid mutation helpers — remove/insert size (row) & measure (column).
    // `values` is keyed by `ri-ci`; after a row/column removal we compress the
    // remaining keys so they map cleanly onto the new indices.  Public API
    // is the *Intent* variant: opens a confirmation dialog surfacing how many
    // cm values will be lost. The confirmed removal is applied by
    // `applyRemoveSizeAt`/`applyRemoveMeasureAt`.
    const removeSizeAt = (idx) => {
        const name = localSizes[idx];
        const cmCount = countAxisValues(values, idx, "row", localMeasures.length);
        setPendingRowDelete({ idx, name, cmCount });
    };
    const removeMeasureAt = (idx) => {
        const name = localMeasures[idx];
        const cmCount = countAxisValues(values, idx, "col", localSizes.length);
        setPendingColDelete({ idx, name, cmCount });
    };
    const applyRemoveSizeAt = (idx) => {
        const removed = localSizes[idx];
        const nextSizes = localSizes.filter((_, i) => i !== idx);
        setLocalSizes(nextSizes);
        setValues((prev) => {
            const next = {};
            Object.entries(prev).forEach(([k, v]) => {
                const [ri, ci] = k.split("-").map(Number);
                if (ri < idx) next[`${ri}-${ci}`] = v;
                else if (ri > idx) next[`${ri - 1}-${ci}`] = v;
            });
            return next;
        });
        toast.success(`"${removed}" bedeni bu tablodan çıkarıldı`);
    };
    const applyRemoveMeasureAt = (idx) => {
        const removed = localMeasures[idx];
        const nextMeasures = localMeasures.filter((_, i) => i !== idx);
        setLocalMeasures(nextMeasures);
        setValues((prev) => {
            const next = {};
            Object.entries(prev).forEach(([k, v]) => {
                const [ri, ci] = k.split("-").map(Number);
                if (ci < idx) next[`${ri}-${ci}`] = v;
                else if (ci > idx) next[`${ri}-${ci - 1}`] = v;
            });
            return next;
        });
        toast.success(`"${removed}" ölçüsü bu tablodan çıkarıldı`);
    };
    // Drag-drop reorder callbacks — swap positions inside the local size /
    // measure arrays AND remap the `values` grid so cm values follow their
    // row/column to the new index.
    const handleRowDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = localSizes.indexOf(active.id);
        const to = localSizes.indexOf(over.id);
        if (from < 0 || to < 0) return;
        const next = arrayMove(localSizes, from, to);
        setLocalSizes(next);
        setValues((prev) => remapValuesAfterReorder(prev, localSizes, next, "row"));
    };
    const handleColDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = localMeasures.indexOf(active.id);
        const to = localMeasures.indexOf(over.id);
        if (from < 0 || to < 0) return;
        const next = arrayMove(localMeasures, from, to);
        setLocalMeasures(next);
        setValues((prev) => remapValuesAfterReorder(prev, localMeasures, next, "col"));
    };
    const addSize = (name) => {
        setLocalSizes((prev) => [...prev, name]);
        toast.success(`"${name}" bedeni eklendi — cm değerlerini girmeyi unutma`);
    };
    const addMeasure = (name) => {
        setLocalMeasures((prev) => [...prev, name]);
        // Pull the kind from the source group so the saved measure stays
        // structured. Falls back to "product" at save-time if nothing matches.
        const srcMeasure = (sourceGroup?.measures || []).find((m) => (typeof m === "string" ? m : m?.name) === name);
        const kind = srcMeasure && typeof srcMeasure === "object" ? srcMeasure.kind : undefined;
        if (kind) setMeasureKinds((prev) => ({ ...prev, [name]: kind }));
        toast.success(`"${name}" ölçüsü eklendi — cm değerlerini girmeyi unutma`);
    };

    const handleCellKey = (e, ri, ci, maxRi) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const next = ri + 1 < maxRi ? cellRefs.current[`${ri + 1}-${ci}`] : null;
            next?.focus();
            next?.select();
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
                <SheetContent side="right" className="w-full sm:max-w-[680px] p-0 gap-0 flex flex-col overflow-hidden" data-testid="edit-table-dialog">
                    <div className="px-6 py-5 border-b border-border flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">Beden Tablosu</div>
                            {renaming ? (
                                <div className="flex items-center gap-1.5 mt-1">
                                    <SheetTitle className="sr-only">{table.name}</SheetTitle>
                                    <Input
                                        autoFocus
                                        value={nameDraft}
                                        onChange={(e) => setNameDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") saveRename();
                                            if (e.key === "Escape") setRenaming(false);
                                        }}
                                        className="h-8 text-sm font-semibold max-w-[300px]"
                                        data-testid="table-name-input"
                                    />
                                    <Button size="icon" className="h-8 w-8 bg-brand hover:bg-brand/90 text-brand-fg shrink-0" onClick={saveRename} data-testid="rename-save-btn">
                                        <Check className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setRenaming(false)} data-testid="rename-cancel-btn">
                                        <X className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 mt-1">
                                    <SheetTitle className="font-display text-xl font-bold tracking-tight text-foreground">{table.name}</SheetTitle>
                                    <button
                                        onClick={() => { setNameDraft(table.name); setRenaming(true); }}
                                        className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                                        title="Tablo adını düzenle"
                                        aria-label="Tablo adını düzenle"
                                        data-testid="rename-table-btn"
                                    >
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    {isShared && <Badge variant="outline" className="text-[10px]">{count} ürün</Badge>}
                                    {count === 0 && <Badge variant="outline" className="text-[10px] border-warning/40 text-warning bg-warning-soft">eşleşme bekliyor</Badge>}
                                </div>
                            )}
                            <div className="text-xs text-muted-foreground mt-1">
                                {table.seriesLabel} · {(table.sizes || []).length} beden · {table.groupLabel} · {(table.measures || []).length} ölçü · {timeAgo(table.updated)}
                            </div>
                        </div>
                    </div>

                    {isShared && (
                        <div className="px-6 py-3 border-b border-border bg-muted/30" data-testid="shared-banner">
                            <div className="flex items-start gap-3">
                                <Info className="h-4 w-4 text-foreground/70 shrink-0 mt-0.5" strokeWidth={1.75} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-foreground">Bu tablo {count} ürün tarafından paylaşılıyor</div>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                                        Buradaki değişiklikler {count} ürünün tamamına yansır.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="px-6 py-5 flex-1 overflow-y-auto scroll-thin">
                        {/* Row / column mutation toolbar. Shows the merchant's ability
                            to add or remove sizes and measures constrained to the
                            source series/group template. Snapshot pattern: changes
                            apply to THIS table only. */}
                        <div className="flex items-center justify-end gap-2 mb-2 flex-wrap" data-testid="row-col-toolbar">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        size="sm" variant="outline"
                                        className="h-7 text-xs gap-1.5"
                                        disabled={!sourceSeries || availableSizes.length === 0}
                                        title={!sourceSeries ? "Kaynak seri bulunamadı" : availableSizes.length === 0 ? "Kaynak serideki tüm bedenler zaten tabloda" : ""}
                                        data-testid="add-size-btn"
                                    >
                                        <Plus className="h-3 w-3" /> Beden Ekle
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-56 p-2">
                                    <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground px-2 pb-1.5">
                                        {sourceSeries?.name} · Kullanılabilir bedenler
                                    </div>
                                    <div className="max-h-56 overflow-y-auto scroll-thin">
                                        {availableSizes.map((sz) => (
                                            <button
                                                key={sz}
                                                onClick={() => addSize(sz)}
                                                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted transition-colors text-sm font-display font-bold"
                                                data-testid={`add-size-${sz}`}
                                            >
                                                {sz}
                                            </button>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        size="sm" variant="outline"
                                        className="h-7 text-xs gap-1.5"
                                        disabled={!sourceGroup || availableMeasures.length === 0}
                                        title={!sourceGroup ? "Kaynak grup bulunamadı" : availableMeasures.length === 0 ? "Kaynak gruptaki tüm ölçüler zaten tabloda" : ""}
                                        data-testid="add-measure-btn"
                                    >
                                        <Plus className="h-3 w-3" /> Ölçü Ekle
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-56 p-2">
                                    <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground px-2 pb-1.5">
                                        {sourceGroup?.name} · Kullanılabilir ölçüler
                                    </div>
                                    <div className="max-h-56 overflow-y-auto scroll-thin">
                                        {availableMeasures.map((m) => (
                                            <button
                                                key={m}
                                                onClick={() => addMeasure(m)}
                                                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted transition-colors text-xs"
                                                data-testid={`add-measure-${m}`}
                                            >
                                                {m}
                                            </button>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="rounded-lg border border-border overflow-hidden">
                            {/* Column drag context — header only. Each measure header can be dragged
                                horizontally to reorder columns. */}
                            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleColDragEnd}>
                                <SortableContext items={localMeasures} strategy={horizontalListSortingStrategy}>
                                    <div className="grid bg-muted/40 text-[10px] font-semibold tracking-wider uppercase text-muted-foreground border-b border-border" style={{ gridTemplateColumns: `100px repeat(${localMeasures.length}, minmax(0,1fr))` }}>
                                        <div className="px-3 py-2">Beden</div>
                                        {localMeasures.map((m, ci) => (
                                            <SortableColHeader key={m} id={m} className="px-3 py-2 flex items-center gap-1 group">
                                                {({ attributes, listeners }) => (
                                                    <>
                                                        <button
                                                            type="button"
                                                            {...attributes}
                                                            {...listeners}
                                                            className="h-4 w-3.5 -ml-1 shrink-0 flex items-center justify-center text-muted-foreground/40 hover:text-foreground cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 transition-opacity"
                                                            aria-label={`${m} sütununu sürükle`}
                                                            title="Sütun sırasını değiştirmek için sürükle"
                                                            data-testid={`drag-measure-${ci}`}
                                                        >
                                                            <GripVertical className="h-3 w-3" strokeWidth={1.75} />
                                                        </button>
                                                        <span className="truncate flex-1">{m} <span className="lowercase text-muted-foreground/60">(cm)</span></span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeMeasureAt(ci)}
                                                            className="h-5 w-5 rounded grid place-items-center text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                                            aria-label={`${m} ölçüsünü çıkar`}
                                                            title="Bu ölçüyü tablodan çıkar"
                                                            data-testid={`remove-measure-${ci}`}
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </>
                                                )}
                                            </SortableColHeader>
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                            {/* Row drag context — each size row can be dragged vertically to
                                reorder rows. Cells (inputs) are preserved and remapped so cm
                                values follow their size. */}
                            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleRowDragEnd}>
                                <SortableContext items={localSizes} strategy={verticalListSortingStrategy}>
                                    {localSizes.map((sz, ri) => (
                                        <SortableGridRow
                                            key={sz}
                                            id={sz}
                                            className="grid items-center border-b border-border last:border-0 text-sm hover:bg-muted/30 group bg-background"
                                            style={{ gridTemplateColumns: `100px repeat(${localMeasures.length}, minmax(0,1fr))` }}
                                        >
                                            {({ attributes, listeners }) => (
                                                <>
                                                    <div className="px-2 py-2 flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            {...attributes}
                                                            {...listeners}
                                                            className="h-5 w-3.5 shrink-0 flex items-center justify-center text-muted-foreground/40 hover:text-foreground cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 transition-opacity"
                                                            aria-label={`${sz} satırını sürükle`}
                                                            title="Satır sırasını değiştirmek için sürükle"
                                                            data-testid={`drag-size-${ri}`}
                                                        >
                                                            <GripVertical className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                        </button>
                                                        <span className="font-display font-bold flex-1 truncate">{sz}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeSizeAt(ri)}
                                                            className="h-5 w-5 rounded grid place-items-center text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                                            aria-label={`${sz} bedenini çıkar`}
                                                            title="Bu bedeni tablodan çıkar"
                                                            data-testid={`remove-size-${ri}`}
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                    {localMeasures.map((m, ci) => (
                                                        <div key={`${sz}-${m}`} className="px-2 py-1.5">
                                                            <input
                                                                ref={(el) => { cellRefs.current[`${ri}-${ci}`] = el; }}
                                                                type="number"
                                                                placeholder="—"
                                                                value={values[`${ri}-${ci}`] || ""}
                                                                onChange={(e) => setValues((v) => ({ ...v, [`${ri}-${ci}`]: e.target.value }))}
                                                                onPaste={(e) => {
                                                                    const text = e.clipboardData.getData("text");
                                                                    if (!text || (!text.includes("\t") && !text.includes("\n") && !text.includes(";"))) return;
                                                                    e.preventDefault();
                                                                    const { next, filled } = gridPaste(values, text, ri, ci, localSizes.length, localMeasures.length);
                                                                    setValues(next);
                                                                    toast.success(`${filled} hücre yapıştırıldı`);
                                                                }}
                                                                onKeyDown={(e) => handleCellKey(e, ri, ci, localSizes.length)}
                                                                className="w-full h-8 bg-background border border-transparent hover:border-border focus:border-foreground/30 rounded-md px-2 text-sm font-mono num outline-none placeholder:text-muted-foreground/40"
                                                                data-testid={`edit-cell-${ri}-${ci}`}
                                                            />
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </SortableGridRow>
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-2 inline-flex items-center gap-1.5">
                            <Info className="h-3 w-3" /> Satır veya sütunu <b className="mx-0.5">sürükleyerek</b> sırasını değiştir · Excel'den kopyaladığın bloğu bir hücreye yapıştırabilirsin.
                        </p>

                        {/* Linked products — interactive (add / detach) */}
                        <div className="mt-4 rounded-lg border border-border overflow-hidden" data-testid="linked-products-section">
                            <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <Package className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                                    <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">Bağlı ürünler</span>
                                    <Badge variant="outline" className="text-[10px] font-mono num">{count}</Badge>
                                </div>
                                <Button
                                    size="sm" variant="outline"
                                    className="h-7 text-xs gap-1.5"
                                    onClick={() => setLinkModalOpen(true)}
                                    data-testid="open-link-products-modal"
                                >
                                    <Plus className="h-3 w-3" /> Ürün ekle
                                </Button>
                            </div>

                            {count === 0 ? (
                                <div className="px-4 py-6 text-center" data-testid="linked-products-empty">
                                    <p className="text-xs text-muted-foreground">Henüz ürün bağlanmadı — bu tablo <span className="text-warning font-medium">eşleşme bekliyor</span>.</p>
                                </div>
                            ) : (
                                <>
                                    {count > 5 && (
                                        <div className="px-4 py-2 border-b border-border bg-muted/10">
                                            <div className="relative">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                                                <Input
                                                    value={linkedQuery}
                                                    onChange={(e) => setLinkedQuery(e.target.value)}
                                                    placeholder="Bağlı ürünlerde ara…"
                                                    className="h-8 pl-8 text-xs bg-background"
                                                    data-testid="linked-products-search"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="max-h-[220px] overflow-y-auto scroll-thin" data-testid="linked-products-list">
                                        {linkedProducts.length === 0 ? (
                                            <p className="text-xs text-muted-foreground py-4 text-center">"{linkedQuery.trim()}" ile eşleşen bağlı ürün yok.</p>
                                        ) : linkedProducts.map((p) => (
                                            <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0" data-testid={`linked-row-${p.id}`}>
                                                <div className="h-8 w-8 rounded border border-border bg-muted overflow-hidden shrink-0">
                                                    {p.image && <img src={p.image} alt="" className="h-full w-full object-cover" />}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-xs font-medium truncate">{p.name}</div>
                                                    <div className="text-[10px] font-mono text-muted-foreground">{p.id}</div>
                                                </div>
                                                <span className="text-[10px] text-muted-foreground shrink-0 hidden sm:block">{p.category}</span>
                                                <button
                                                    onClick={() => setPendingRemove(p)}
                                                    className="text-muted-foreground hover:text-destructive shrink-0 p-1"
                                                    title="Tablodan ayır"
                                                    data-testid={`unlink-product-${p.id}`}
                                                >
                                                    <Unlink2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-between">
                        {ctxProduct ? (
                            <Button
                                variant="ghost" size="sm"
                                className="h-9 text-xs gap-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                onClick={() => setConfirmDetach(true)}
                                data-testid="detach-btn"
                            >
                                <Unlink2 className="h-3 w-3" /> Ürünü Tablodan Ayır
                            </Button>
                        ) : (
                            <Button
                                variant="ghost" size="sm"
                                className="h-9 text-xs gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => setConfirmDelete(true)}
                                data-testid="delete-btn"
                            >
                                <Trash2 className="h-3 w-3" /> Tabloyu Sil
                            </Button>
                        )}
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={onClose} disabled={saving} className="h-9 text-xs">İptal</Button>
                            <Button
                                size="sm"
                                onClick={handleSave}
                                disabled={saving}
                                className="h-9 text-xs gap-1.5 bg-brand hover:bg-brand/90 text-brand-fg disabled:opacity-60 disabled:pointer-events-none"
                                data-testid="edit-save"
                            >
                                {saving ? (
                                    <><Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor…</>
                                ) : (
                                    <><Check className="h-3 w-3" /> Kaydet</>
                                )}
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <DeleteConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                itemName={table.name}
                impactCount={count}
                impactMessage={
                    <>
                        <strong>{count} ürün</strong> bu tabloya bağlı. Silersen bu ürünler beden tablosuz kalır ve mağazanda beden tablosu widget&apos;ı gösterilmez.
                    </>
                }
                permanentNote="Tablo ve tüm ölçüm değerleri kalıcı olarak silinir. Bu işlem geri alınamaz."
                ackMessage={<><strong>{count} ürün</strong> bu silme sonrası beden tablosuz kalacak.</>}
                onConfirm={() => {
                    setConfirmDelete(false);
                    removeTable(table.id);
                    toast.success(`"${table.name}" silindi${count > 0 ? ` · ${count} ürün tablosuz kaldı` : ""}`);
                    onClose();
                }}
                testId="table-delete"
            />

            <AlertDialog open={confirmDetach} onOpenChange={setConfirmDetach}>
                <AlertDialogContent data-testid="product-detach-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Ürünü Tablodan Ayır</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">{ctxProduct?.name}</span> <span className="font-mono">({ctxProduct?.id})</span>, <span className="font-medium text-foreground">{table.name}</span> tablosundan ayrılacak. Tablo ve diğer {Math.max(count - 1, 0)} ürün etkilenmez; bu ürün beden tablosuz kalır, istediğin zaman yeniden bağlayabilirsin.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { setConfirmDetach(false); unlinkProduct(ctxProduct.id); toast.success(`${ctxProduct?.id}, "${table.name}" tablosundan ayrıldı`); onClose(); }} data-testid="product-detach-confirm">
                            Evet, ayır
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!pendingRemove} onOpenChange={(o) => !o && setPendingRemove(null)}>
                <AlertDialogContent data-testid="row-unlink-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Ürünü Tablodan Ayır</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-medium text-foreground">{pendingRemove?.name}</span> <span className="font-mono">({pendingRemove?.id})</span>, bu tablodan ayrılacak. Ürün beden tablosuz kalır ve mağazanda beden tablosu widget'ı gösterilmez; istediğin zaman yeniden bağlayabilirsin.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmRemoveProduct} data-testid="row-unlink-confirm">
                            Evet, ayır
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Row (size) delete confirmation — surfaces how many cm values will
                be lost so the merchant never removes a size accidentally. */}
            <AlertDialog open={!!pendingRowDelete} onOpenChange={(o) => !o && setPendingRowDelete(null)}>
                <AlertDialogContent data-testid="row-delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            "{pendingRowDelete?.name}" bedenini tablodan çıkar?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {pendingRowDelete?.cmCount > 0 ? (
                                <>
                                    Bu satırda <span className="font-semibold text-foreground">{pendingRowDelete?.cmCount} cm değeri</span> girilmiş —
                                    bedeni çıkarırsan bu değerler <span className="font-semibold text-destructive">silinir</span> ve geri alınamaz. Kaynak beden serisi etkilenmez, sadece bu tabloya özgü kayıt silinir.
                                </>
                            ) : (
                                <>Bu tabloya özgü kayıt silinir. Kaynak beden serisi etkilenmez.</>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="row-delete-cancel">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => {
                                applyRemoveSizeAt(pendingRowDelete.idx);
                                setPendingRowDelete(null);
                            }}
                            data-testid="row-delete-confirm"
                        >
                            Evet, çıkar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Column (measure) delete confirmation. */}
            <AlertDialog open={!!pendingColDelete} onOpenChange={(o) => !o && setPendingColDelete(null)}>
                <AlertDialogContent data-testid="col-delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            "{pendingColDelete?.name}" ölçüsünü tablodan çıkar?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {pendingColDelete?.cmCount > 0 ? (
                                <>
                                    Bu sütunda <span className="font-semibold text-foreground">{pendingColDelete?.cmCount} cm değeri</span> girilmiş —
                                    ölçüyü çıkarırsan bu değerler <span className="font-semibold text-destructive">silinir</span> ve geri alınamaz. Kaynak ölçü grubu etkilenmez, sadece bu tabloya özgü kayıt silinir.
                                </>
                            ) : (
                                <>Bu tabloya özgü kayıt silinir. Kaynak ölçü grubu etkilenmez.</>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="col-delete-cancel">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => {
                                applyRemoveMeasureAt(pendingColDelete.idx);
                                setPendingColDelete(null);
                            }}
                            data-testid="col-delete-confirm"
                        >
                            Evet, çıkar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Ürün bağlama akışı — geniş, thumbnail'li kendi modal'ında. */}
            <LinkProductsModal
                open={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                table={table}
                products={products}
                links={links}
                tables={tables}
                linkProduct={linkProduct}
                onLinked={handleBulkLinked}
            />
        </>
    );
};

// ----------------- Bulk link dialog -----------------
const BulkLinkDialog = ({ payload, onClose }) => {
    const { tables, links, linkProduct } = useSizing();
    const [pickedId, setPickedId] = useState(null);
    useEffect(() => { if (!payload) setPickedId(null); }, [payload]);
    if (!payload) return null;
    const { ids } = payload;

    const picked = pickedId ? findTable(tables, pickedId) : null;
    const movedIds = picked ? ids.filter((id) => links[id] && links[id] !== picked.id) : [];

    const handleBulkLink = () => {
        ids.forEach((id) => linkProduct(id, picked.id));
        toast.success(
            `${ids.length} ürün "${picked.name}" tablosuna bağlandı${movedIds.length ? ` · ${movedIds.length} ürün taşındı` : ""}`
        );
        onClose();
    };

    return (
        <Sheet open onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-xl p-0 gap-0 flex flex-col overflow-hidden" data-testid="bulk-link-dialog">
                <div className="px-6 py-5 border-b border-border">
                    <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">Toplu işlem</div>
                    <SheetTitle className="font-display text-lg font-bold tracking-tight mt-1 text-foreground">
                        {ids.length} ürünü mevcut tabloya bağla
                    </SheetTitle>
                    <div className="mt-2 flex flex-wrap gap-1">
                        {ids.slice(0, 8).map((id) => (
                            <Badge key={id} variant="outline" className="text-[10px] font-mono">{id}</Badge>
                        ))}
                        {ids.length > 8 && <span className="text-[10px] text-muted-foreground">+{ids.length - 8} daha</span>}
                    </div>
                </div>
                <div className="px-6 py-4 flex-1 overflow-y-auto scroll-thin space-y-2">
                    {tables.map((t) => {
                        const selected = pickedId === t.id;
                        const cnt = countLinked(links, t.id);
                        return (
                            <button
                                key={t.id}
                                onClick={() => setPickedId(t.id)}
                                className={`w-full text-left rounded-lg border p-3 transition-all ${selected ? "border-foreground/40 bg-muted/40" : "border-border bg-surface hover:border-foreground/20"}`}
                                data-testid={`bulk-option-${t.id}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-start gap-2.5 min-w-0">
                                        <Table2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5" strokeWidth={1.75} />
                                        <div className="min-w-0">
                                            <div className="font-display font-bold tracking-tight text-sm">{t.name}</div>
                                            <div className="text-[10px] text-muted-foreground">{t.seriesLabel} · {t.groupLabel}{cnt === 0 ? " · eşleşme bekliyor" : ` · ${cnt} ürün`}</div>
                                        </div>
                                    </div>
                                    <div className={`h-4 w-4 rounded-full grid place-items-center shrink-0 ${selected ? "bg-foreground" : "border border-border"}`}>
                                        {selected && <Check className="h-2.5 w-2.5 text-background" strokeWidth={3} />}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
                {picked && movedIds.length > 0 && (
                    <div className="px-6 py-3 border-t border-border bg-warning-soft/50" data-testid="bulk-warnings">
                        <p className="text-[11px] text-warning font-medium">
                            {movedIds.length} ürün mevcut tablosundan bu tabloya taşınacak.
                        </p>
                    </div>
                )}
                <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={onClose} className="h-9 text-xs">İptal</Button>
                    <Button
                        size="sm"
                        disabled={!pickedId}
                        onClick={handleBulkLink}
                        className="h-9 text-xs gap-1.5 bg-brand hover:bg-brand/90 text-brand-fg"
                        data-testid="bulk-link-save"
                    >
                        <Check className="h-3 w-3" /> {ids.length} ürünü bağla
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};
